import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Avatar, Modal, Box, Switch, Typography, Divider, List, ListItem, ListItemAvatar, ListItemText, Card, CardContent, CircularProgress, Button, Menu, MenuItem, IconButton, Paper, Grid, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, FormControl, InputLabel, Select } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedIcon from '@mui/icons-material/Verified';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReportIcon from '@mui/icons-material/Report';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChatIcon from '@mui/icons-material/Chat';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import moment from 'moment';
import Post from '../components/Post';
import './Profile.css';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService, clearUserCache, refreshProfileCache, safeApiCall, normalizeImageUrl } from '../utils/api';
import config from '../config';
import BusinessIcon from '@mui/icons-material/Business';
import Chip from '@mui/material/Chip';
import PhoneIcon from '@mui/icons-material/Phone';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WorkIcon from '@mui/icons-material/Work';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameDay, isSameMonth, addDays, subMonths } from 'date-fns';
import { ja } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { setPageTitle, setMetaTags, generateProfileMeta } from '../utils/seoUtils';
import { ReviewsTab } from '../components/Reviews';

// isPublicProfileのヘルパー関数を1箇所だけに定義
const isPublicProfile = (username) => {
  // 現在はすべてのプロフィールを公開として設定
  return true;
};

// PostDetail.jsと同じ方式のAPIコール関数（401エラー時にリダイレクトしない）
const guestSafeApiCall = async (url, headers = {}, params = {}) => {
  try {
    // PostDetail.jsと同じ方式：トークンがある場合のみ認証ヘッダーを追加
    const token = localStorage.getItem('token');
    const finalHeaders = token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
    
    const response = await axios.get(`${config.API_BASE_URL}${url}`, {
      headers: finalHeaders,
      params: params
    });
    return response;
  } catch (error) {
    // 401エラーでもリダイレクトせず、エラーを投げる
    if (error.response && error.response.status === 401) {
      console.log('【デバッグ】Profile.js - 401エラー検出（ゲストアクセス対応のためリダイレクトなし）');
      // 401エラーでもデータを返す場合があるので、レスポンスがあれば返す
      if (error.response.data) {
        return error.response;
      }
    }
    throw error;
  }
};

function Profile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // 状態管理を分割
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true); // プロフィール専用ローディング
  const [tabsLoading, setTabsLoading] = useState(true); // タブ専用ローディング
  const [tabsInitialized, setTabsInitialized] = useState(false); // タブの初期化状態
  const [widgetsLoading, setWidgetsLoading] = useState(true); // ウィジェット（サイドバー）専用ローディング
  const [shouldLoadWidgets, setShouldLoadWidgets] = useState(false); // ウィジェット読み込み開始フラグ
  const [posts, setPosts] = useState([]);
  const [replies, setReplies] = useState([]);
  const [mediaPosts, setMediaPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [publicLists, setPublicLists] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    // URL に ?tab= があれば初期タブとして採用（予約タブ等への直接遷移時のちらつき防止）
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      if (t) return t;
    } catch (e) {}
    return 'posts';
  });
  const [loading, setLoading] = useState(true); // 全体ローディング（後方互換性）
  const [error, setError] = useState('');
  
  // 投稿の総数を管理（ヘッダー表示用）
  const [totalPostsCount, setTotalPostsCount] = useState(0);
  
  // 投稿の無限スクロール用状態（5件ずつ）
  const [postsPage, setPostsPage] = useState(1);
  const [postsLoadingMore, setPostsLoadingMore] = useState(false);
  const [postsHasMore, setPostsHasMore] = useState(true);
  const POSTS_PER_PAGE = 5; // 5件ずつ読み込み
  const { currentUser, isLoggedIn, checkPermission } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [followingCount, setFollowingCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowingLoading, setIsFollowingLoading] = useState(false);
  // メッセージ機能用の状態を追加
  const [hasMessageHistory, setHasMessageHistory] = useState(false);
  const [messageHistoryChecking, setMessageHistoryChecking] = useState(false);
  
  // 現在のユーザーが表示しているプロフィールが自分自身かどうかを判定
  const isCurrentUserProfile = currentUser && currentUser.username === username;
  
  // ユーザーブロック状態を管理
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  
  // 各ユーザータイプに応じてデフォルトのタブを調整
  useEffect(() => {
    if (currentUser && profile) {
      // URL の ?tab= が指定されていればそれを優先（予約タブ等への直接遷移用）
      try {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam) {
          setActiveTab(tabParam);
          return;
        }
      } catch (e) {}
      // 自分自身のプロフィールを見ている場合
      if (isCurrentUserProfile) {
        // 閲覧ユーザーの場合は予約タブをデフォルトに
        if (currentUser.role === 'viewer') {
          setActiveTab('reservations');
        }
        // 投稿ユーザーの場合は投稿タブをデフォルトに
        else if (currentUser.role === 'poster' || 
                 profile.user_type === 'poster' || 
                 profile.poster_profile || 
                 profile.posterProfile) {
          setActiveTab('posts');
        }
        // 企業ユーザーの場合も投稿タブをデフォルトに
        else if (currentUser.role === 'corporate') {
          setActiveTab('posts');
        }
        // 管理者の場合も投稿タブをデフォルトに
        else if (currentUser.role === 'admin') {
          setActiveTab('posts');
        }
        // その他のユーザータイプは投稿タブをデフォルトに
        else {
          setActiveTab('posts');
        }
      }
      // 他ユーザーのプロフィールを見ている場合
      else {
        // 閲覧ユーザーのプロフィールを見ている場合は返信タブをデフォルトに
        if (profile.role === 'viewer') {
          setActiveTab('replies');
        }
        // 投稿ユーザーのプロフィールを見ている場合は投稿タブをデフォルトに
        else if (profile.role === 'poster' || 
                 profile.user_type === 'poster' || 
                 profile.poster_profile || 
                 profile.posterProfile) {
          setActiveTab('posts');
        }
        // 企業ユーザーのプロフィールを見ている場合も投稿タブをデフォルトに
        else if (profile.role === 'corporate') {
          setActiveTab('posts');
        }
        // その他のユーザータイプは投稿タブをデフォルトに
        else {
          setActiveTab('posts');
        }
      }
    }
  }, [currentUser, profile, isCurrentUserProfile]);
  
  // 三点メニュー用の状態
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  
  // 無限スクロール用の状態
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastId, setLastId] = useState(null);
  const loadMoreRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  
  // タブ要素への参照を追加
  const tabsContainerRef = useRef(null);
  
  // ユーザー自身のプロフィール表示時に統計情報を取得して表示するセクションを追加
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);

  // 予約関連の状態を追加
  const [reservations, setReservations] = useState([]);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [selectedPoster, setSelectedPoster] = useState(null);
  const [followingPosters, setFollowingPosters] = useState([]);
  // 選択中の投稿ユーザーのシフト一覧（予約可能時間の表示用）
  const [selectedPosterShifts, setSelectedPosterShifts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [reservationDate, setReservationDate] = useState(new Date());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [courseMinutes, setCourseMinutes] = useState(60); // コース分数（デフォルト60分）
  const [hotel, setHotel] = useState(''); // ホテル（場所）
  const [memo, setMemo] = useState('');
  const [openReservationDialog, setOpenReservationDialog] = useState(false);

  // ユーザーアクション状態（一元管理）
  const [userActions, setUserActions] = useState({
    liked_posts: [],
    bookmarked_posts: [],
    retweeted_posts: [],
    loading: false,
    loaded: false
  });

  // ユーザーアクション取得関数（一元化）
  const fetchUserActions = useCallback(async () => {
    if (!isLoggedIn || userActions.loading || userActions.loaded) {
      return;
    }

    try {
      setUserActions(prev => ({ ...prev, loading: true }));
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('ユーザーアクション取得: トークンなし');
        return;
      }

      console.log('=== ユーザーアクション取得開始（Profile） ===');
      
      // PostDetail.jsと同じ方式で直接axiosを使用
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${config.API_BASE_URL}/api/posts/user-actions`, {
        headers
      });
      
      const data = response.data || { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      
      setUserActions({
        liked_posts: data.liked_posts || [],
        bookmarked_posts: data.bookmarked_posts || [],
        retweeted_posts: data.retweeted_posts || [],
        loading: false,
        loaded: true
      });
      
      console.log('ユーザーアクション取得完了（Profile）:', {
        liked: data.liked_posts?.length || 0,
        bookmarked: data.bookmarked_posts?.length || 0,
        retweeted: data.retweeted_posts?.length || 0
      });
      
      // 詳細なデバッグ情報を出力
      console.log('ユーザーアクション詳細（Profile）:', {
        liked_posts: data.liked_posts,
        bookmarked_posts: data.bookmarked_posts,
        retweeted_posts: data.retweeted_posts
      });
      
    } catch (error) {
      console.error('ユーザーアクション取得エラー（Profile）:', error);
      setUserActions({
        liked_posts: [],
        bookmarked_posts: [],
        retweeted_posts: [],
        loading: false,
        loaded: false
      });
    }
  }, [isLoggedIn, userActions.loading, userActions.loaded]);

  // プロフィール情報と投稿の取得
  useEffect(() => {
    // 別ユーザーのプロフィールに切り替わった時にページ上部にスクロール
    window.scrollTo(0, 0);
    
    // 初期SEO設定
    setPageTitle(`プロフィール ${username || 'ユーザー'}`);
    setMetaTags({
      title: `プロフィール ${username || 'ユーザー'}`,
      description: `${username || 'ユーザー'}さんのプロフィールページ`,
      type: 'profile'
    });
    
    const fetchProfileData = async () => {
      try {
        setProfileLoading(true); // プロフィール専用ローディングを使用
        setError('');
        setPage(1); // ページをリセット
        setHasMore(true); // 追加データがあることをリセット
        
        // パフォーマンスの問題が発生した場合にキャッシュをクリア
        refreshProfileCache(username);
        
        // ユーザープロフィールキャッシュもクリア（投稿一覧混在防止）
        if (typeof window !== 'undefined' && window.userProfileCache) {
          window.userProfileCache.clear();
        }
        
        // PostDetail.jsと同様の方法でトークンとヘッダーを設定
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // PostDetail.jsと同様に直接axiosを使用（401エラー時のリダイレクトを回避）
        const profileResponse = await axios.get(`${config.API_BASE_URL}/api/users/${username}`, { 
          headers,
          params: {
            _t: Date.now() // キャッシュ回避のためのタイムスタンプ
          }
        });
        
        // プロフィール基本情報を先に設定してUI表示を開始
        setProfile(profileResponse.data);
        setProfileLoading(false); // プロフィール専用ローディング完了
        
        // プロフィール表示完了後にウィジェット読み込みを開始
        setTimeout(() => {
          setShouldLoadWidgets(true);
        }, 500); // 500ms後にウィジェット読み込み開始
        
        // デバッグ用：レスポンスの詳細をログ出力
        
        
        // HTMLレスポンスをチェック（緩和した条件）
        if (profileResponse.data && typeof profileResponse.data === 'string' && 
            profileResponse.data.includes('<!DOCTYPE html>') && 
            profileResponse.data.includes('<html')) {
          
          
          // トークンを再取得して検証
          const newToken = localStorage.getItem('authToken');
          if (newToken !== token) {
            // トークンが更新されていたら、再試行
            
            // 再帰的に関数を呼び出して、新しいトークンでリトライ
            fetchProfileData();
            return;
          }
          
          // 認証エラーと判断される場合（ゲストアクセス対応のため無効化）
          if (profileResponse.data.includes('login') || 
              profileResponse.data.includes('Login') || 
              profileResponse.data.includes('ログイン')) {
            console.log('【デバッグ】HTMLレスポンス内にログイン文字列検出 - リダイレクトは無効化');
            
            // ゲストアクセス対応のため、エラーメッセージのみ表示
            setError('プロフィール情報の取得に問題が発生しました。しばらく後に再度お試しください。');
            return;
          }
          
          throw new Error('APIからHTMLが返されました。認証問題または権限の問題かもしれません。');
        }
        
        // レスポンスデータがnullやundefinedの場合にも明示的にエラーを投げる
        if (!profileResponse.data) {
          
          throw new Error('プロフィールデータを取得できませんでした。');
        }
        
        // プロフィールレスポンスのブロック状態を確認
        if (profileResponse.data.is_blocked !== undefined) {
          
          setIsBlocked(profileResponse.data.is_blocked);
        }
                
        // 相手からブロックされている場合のエラーハンドリング
        if (profileResponse.data.is_blocking_me) {
          
          setError('このユーザーからブロックされているため、プロフィールを表示できません');
          setLoading(false);
          return;
        }
        
        // 企業プロフィール情報の確認
        if (profileResponse.data.role === 'corporate' && profileResponse.data.corporateProfile) {
          
          // プロフィールデータに企業情報を追加
          profileResponse.data.company = profileResponse.data.corporateProfile;
        }
        
        // プロフィールデータを設定
        setProfile(profileResponse.data);
        
        // SEO設定：プロフィールページのタイトルとメタタグを設定
        const meta = generateProfileMeta(profileResponse.data);
        setPageTitle(meta.title, true);
        setMetaTags(meta);
        
        // 企業プロフィール情報のデバッグ出力
        if (profileResponse.data.role === 'corporate' && profileResponse.data.corporateProfile) {
          console.log('企業プロフィール情報:', {
            officialLine: profileResponse.data.corporateProfile.officialLine,
            jobPostingUrl: profileResponse.data.corporateProfile.jobPostingUrl,
            shopName: profileResponse.data.corporateProfile.shop_name,
            fullData: profileResponse.data.corporateProfile
          });
        }
        
        // プロフィールデータのデバッグ情報を出力
        
        
        
        // フォロー状態を確認・設定
        if (isLoggedIn && currentUser && currentUser.username !== username) {
          // フォロー状態をAPIから取得（PostDetail.jsと同じ方式で直接axios使用）
          try {
            // トークンを両方のキーで確認（フォールバック機能）
            const authToken = localStorage.getItem('authToken');
            const tokenFallback = localStorage.getItem('token');
            const followToken = authToken || tokenFallback;
            const followHeaders = followToken ? { Authorization: `Bearer ${followToken}` } : {};
            
            // デバッグ: トークンの確認
            console.log('フォロー状態確認 - isLoggedIn:', isLoggedIn);
            console.log('フォロー状態確認 - currentUser:', currentUser);
            console.log('フォロー状態確認 - targetUsername:', username);
            console.log('フォロー状態確認 - authToken:', authToken);
            console.log('フォロー状態確認 - tokenFallback:', tokenFallback);
            console.log('フォロー状態確認 - followToken:', followToken);
            console.log('フォロー状態確認 - followHeaders:', followHeaders);
            
            const followCheckResponse = await axios.get(`${config.API_BASE_URL}/api/social/is_following/${username}`, {
              headers: followHeaders
            });
            
            // 応答からフォロー状態を設定
            if (followCheckResponse && followCheckResponse.data) {
              setIsFollowing(followCheckResponse.data.is_following === true);
              console.log('フォロー状態確認成功:', followCheckResponse.data.is_following);
            }
          } catch (error) {
            console.error('フォロー状態確認エラー:', error);
            console.error('フォロー状態確認エラーレスポンス:', error.response);
          }
        }
        
        // 投稿ユーザープロフィールの検索
        let posterProfileData = null;
        if (profileResponse.data.poster_profile) {
          posterProfileData = profileResponse.data.poster_profile;
        } else if (profileResponse.data.posterProfile) {
          posterProfileData = profileResponse.data.posterProfile;
        }
        
        
        // 企業アカウント情報をデバッグ出力
        if (posterProfileData) {
          
          
          // 源氏名（stage_name）のデバッグ出力
          
        }
        
        // poster_profileをセット（API応答形式の違いに対応）
        if (posterProfileData && !profileResponse.data.poster_profile) {
          profileResponse.data.poster_profile = posterProfileData;
        }
        
        // 投稿を取得（最初の5件）- PostDetail.jsと同様の方法で
        const postsResponse = await axios.get(`${config.API_BASE_URL}/api/posts/user/${username}`, { 
          headers,
          params: {
            page: 1, 
            per_page: 5,
            exclude_advertisements: true,
            include_count: true // 総投稿数も取得
          }
        });

        // ユーザー投稿の広告除外デバッグ情報をコンソールに表示
        if (postsResponse.data.user_posts_debug) {
          console.log('🚨 === ユーザー投稿の広告除外デバッグ情報 ===');
          const debug = postsResponse.data.user_posts_debug;
          
          console.log('広告投稿ID一覧:', debug.ad_post_ids);
          console.log('広告投稿数:', debug.ad_posts_count);
          console.log('フィルター適用状況:', debug.filter_applied ? '適用済み' : '未適用');
          console.log('元の投稿数:', debug.original_posts_count);
          console.log('リツイート数:', debug.retweets_count);
          console.log('=== ユーザー投稿デバッグ情報終了 ===');
        }

        // デバッグ用：投稿レスポンスの詳細をログ出力
        

        // HTMLレスポンスをチェック（緩和した条件）
        if (postsResponse.data && typeof postsResponse.data === 'string' && 
            postsResponse.data.includes('<!DOCTYPE html>') && 
            postsResponse.data.includes('<html')) {
          
          throw new Error('投稿APIからHTMLが返されました。認証問題または権限の問題かもしれません。');
        }

        // postsResponseがない場合のセーフガード
        if (!postsResponse.data) {
          
          // 空の投稿リストを設定（エラーではなく空の状態として処理）
          setPosts([]);
          setMediaPosts([]);
          setHasMore(false);
          setTotalPostsCount(0); // 総投稿数も0に設定
        } else {
          // 投稿データを設定（posts配列がない場合は空配列を使用）
          const postsList = postsResponse.data.posts || [];
          setPosts(postsList);
          
          // 総投稿数を設定
          setTotalPostsCount(postsResponse.data.total_count || postsResponse.data.total || postsList.length);
          
          // 最後の投稿IDを保存して、次の取得に使用
          if (postsList.length > 0) {
            setLastId(postsList[postsList.length - 1].id);
          }
          
          // 投稿が5件未満の場合は追加データがないと判断
          if (postsList.length < 5) {
            setHasMore(false);
          }
          
          // メディア投稿のみをフィルタリング
          const mediaPostsList = postsList.filter(post => 
            post.image || post.video || 
            (post.media && post.media.length > 0) || 
            post.image_url
          );
          setMediaPosts(mediaPostsList);
        }
        
        // 現在のユーザーのプロフィールの場合、統計情報を表示するフラグを設定
        if (isCurrentUserProfile) {
          setShowStats(true);
          // デフォルトのタブをロール別に設定する。
          // 閲覧ユーザーには「投稿タブ」が存在しないため posts を強制すると
          // どのタブも選択されない状態になる。閲覧ユーザーは予約タブをデフォルトにする。
          // ※ URL に ?tab= が指定されている場合はそれを尊重して上書きしない。
          let hasTabParam = false;
          try {
            hasTabParam = !!new URLSearchParams(location.search).get('tab');
          } catch (e) {}
          if (!hasTabParam) {
            const role = currentUser?.role;
            setActiveTab(role === 'viewer' ? 'reservations' : 'posts');
          }
        }
        
        setLoading(false);
        
      } catch (err) {
        
        // 認証エラーと思われる場合
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          console.log('【デバッグ】Profile.js 401/403エラー検出 - リダイレクトは無効化されています');
          console.log('【デバッグ】Profile.js エラー詳細:', err.response);
          
          // 【一時的無効化】ログイン画面へのリダイレクトを無効化
          /*
          // ログイン後にリダイレクトするために現在のURLを保存
          sessionStorage.setItem('redirect_after_login', window.location.pathname);
          
          setError('認証の有効期限が切れているか、アクセス権限がありません。再度ログインしてください。');
          setTimeout(() => {
            navigate('/login');
          }, 1500);
          */
          
          // 代わりにエラーメッセージのみ表示
          setError('プロフィール情報の取得中に認証エラーが発生しました（一時的に無効化中）');
          
          // 認証エラー時のSEO設定
          setPageTitle('認証エラー');
          setMetaTags({
            title: '認証エラー | デリバリーナビ',
            description: 'プロフィール情報の取得中に認証エラーが発生しました。',
            type: 'profile'
          });
        } else if (err.message.includes('非表示') || (err.response && err.response.data && err.response.data.code === 'USER_HIDDEN')) {
          setError('このユーザーは非表示になっています。');
          
          // エラー時のSEO設定
          setPageTitle('ユーザーが見つかりません');
          setMetaTags({
            title: 'ユーザーが見つかりません | デリバリーナビ',
            description: 'このユーザーは非表示になっています。',
            type: 'profile'
          });
        } else {
          setError('プロフィール情報の取得に失敗しました。しばらく経ってからもう一度お試しください。');
          
          // エラー時のSEO設定
          setPageTitle('プロフィール読み込みエラー');
          setMetaTags({
            title: 'プロフィール読み込みエラー | デリバリーナビ',
            description: 'プロフィール情報の取得に失敗しました。',
            type: 'profile'
          });
        }
        
        setLoading(false);
      }
    };
    
    fetchProfileData();
    
    // フォロー数の取得（ログインユーザーのみ）
    if (isLoggedIn) {
      const fetchFollowCounts = async () => {
        await fetchActualFollowCounts(username);
      };
      
      // フォロー数も非同期に取得（UIブロックはしない）
      fetchFollowCounts();
    }
    
  }, [username, navigate, isCurrentUserProfile]);

  // ユーザーアクション取得を初期化時に実行（ゲストユーザーの場合は無効化）
  useEffect(() => {
    if (isLoggedIn && currentUser && !userActions.loaded && !userActions.loading) {
      fetchUserActions();
    }
  }, [isLoggedIn, currentUser, fetchUserActions, userActions.loaded, userActions.loading]);

  // 投稿の追加読み込み
  const loadMorePosts = useCallback(async () => {
    // 読み込み中またはもうデータがない場合はスキップ
    if (loadingMore || !hasMore) return;
    
    // タブに応じて適切な関数を呼び出し
    if (activeTab === 'posts' || activeTab === 'media') {
      // fetchUserPostsを直接呼び出す代わりに、APIを直接呼び出し
    try {
      setLoadingMore(true);
        const token = localStorage.getItem('authToken');
      const params = { 
          per_page: 5,
        page: page + 1,
        exclude_advertisements: true 
      };
      
      if (lastId) {
        params.before_id = lastId;
      }
      
        // PostDetail.jsと同じ方式で直接axiosを使用
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${config.API_BASE_URL}/api/posts/user/${username}`, {
          headers,
          params
        });

        if (response.data && response.data.posts) {
          const newPosts = response.data.posts;
          
          setPosts(prevPosts => [...prevPosts, ...newPosts]);
          
          if (newPosts.length < params.per_page) {
            setHasMore(false);
          }
          
          if (newPosts.length > 0) {
            setLastId(newPosts[newPosts.length - 1].id);
            setPage(prev => prev + 1);
          }
          
          // メディア投稿も更新
          if (activeTab === 'media') {
            const mediaOnly = newPosts.filter(post => post.image);
            setMediaPosts(prevMedia => [...prevMedia, ...mediaOnly]);
          }
      } else {
          setHasMore(false);
      }
      } catch (error) {
        console.error('追加投稿読み込みエラー:', error);
        setHasMore(false);
      } finally {
        setLoadingMore(false);
      }
        } else if (activeTab === 'replies') {
      await fetchUserReplies(false);
        } else if (activeTab === 'likes') {
      await fetchUserLikedPosts(false);
    }
  }, [activeTab, loadingMore, hasMore, username, page, lastId]);

  // アバターモーダルを開く
  const handleOpenAvatarModal = () => {
    setAvatarModalOpen(true);
  };

  // アバターモーダルを閉じる
  const handleCloseAvatarModal = () => {
    setAvatarModalOpen(false);
  };

  // プロフィール編集ページに移動
  const handleEditProfile = () => {
    // 企業ユーザーの場合は企業プロフィール編集ページへ
    if (currentUser && currentUser.role === 'corporate') {
      navigate('/corporate/profile/edit');
    } else {
      navigate('/profile/edit');
    }
  };

  // タブを切り替え
  const handleTabChange = (tab) => {
    console.log(`=== Profile タブ切り替え: ${activeTab} → ${tab} ===`);
    
    // タブナビゲーション部分にスクロール（データ取得後にも再スクロール）
    const scrollToTabs = () => {
      const tabNav = document.querySelector('.profile__nav');
      if (tabNav) {
        tabNav.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    scrollToTabs();
    // データ取得後の再レンダリングを考慮して遅延スクロールも実行
    setTimeout(scrollToTabs, 300);
    
    // タブが変わる場合、前のタブのキャッシュをクリア
    if (tab !== activeTab) {
      console.log('タブ変更検出 - キャッシュクリア実行');
      
      // 全てのローディング状態をリセット
      setLoading(false);
      setLoadingMore(false);
      setTabsLoading(false);
      
      // ページネーション状態をリセット
      setPage(1);
      setHasMore(true);
      setLastId(null);
      
      // 切り替え先のタブのデータを強制的にクリアして再取得
      switch(tab) {
        case 'posts':
          console.log('投稿タブのキャッシュクリア');
          setPosts([]);
          setPostsPage(1);
          setPostsHasMore(true);
          break;
        case 'replies':
          console.log('返信タブのキャッシュクリア');
          setReplies([]);
          break;
        case 'media':
          console.log('メディアタブのキャッシュクリア');
          setMediaPosts([]);
          break;
        case 'likes':
          console.log('いいねタブのキャッシュクリア');
          setLikedPosts([]);
          break;
        case 'lists':
          console.log('リストタブのキャッシュクリア');
          setPublicLists([]);
          break;
        case 'reservations':
          console.log('予約タブのキャッシュクリア');
          setReservations([]);
          break;
        default:
          break;
      }
    }
    
    // アクティブタブを設定
    setActiveTab(tab);
    
    // タブに応じたデータ取得（キャッシュクリア後なので強制的に再取得）
    console.log(`${tab}タブのデータ取得開始`);
    
    switch(tab) {
      case 'posts':
        fetchUserPosts(username, true); // reset=trueで強制再取得
        break;
      case 'replies':
        fetchUserReplies(true); // reset=trueで強制再取得
        break;
      case 'media':
        fetchUserMediaPosts(); // 常に再取得
        break;
      case 'likes':
        fetchUserLikedPosts(true); // reset=trueで強制再取得
        break;
      case 'lists':
        fetchUserLists(true); // reset=trueで強制再取得
        break;
      case 'shifts':
        fetchShifts(); // 常に再取得
        break;
      case 'reservations':
        if (isCurrentUserProfile) {
          fetchReservations(true); // reset=trueで強制再取得
        }
        break;
      default:
        break;
    }
    
    console.log(`=== タブ切り替え完了: ${tab} ===`);
    
    // タブ切り替え後にタブ位置にスムーズスクロール
    setTimeout(() => {
      if (tabsContainerRef.current) {
        // タブの位置を計算（少し上にオフセット）
        const tabElement = tabsContainerRef.current;
        const tabTop = tabElement.getBoundingClientRect().top + window.pageYOffset;
        
        // デバイスサイズに応じてオフセットを調整
        const isMobile = window.innerWidth <= 768;
        const offset = isMobile ? 80 : 100; // モバイルでは80px、デスクトップでは100px上にオフセット
        
        window.scrollTo({
          top: tabTop - offset,
          behavior: 'smooth'
        });
        
        console.log(`タブ位置にスクロール完了 (オフセット: -${offset}px, デバイス: ${isMobile ? 'モバイル' : 'デスクトップ'})`);
      }
    }, 150); // DOM更新とデータ取得を待つため少し長めの遅延
  };
  
  // 各タブのデータを取得する関数
  const fetchUserReplies = async (reset = true) => {
    try {
      if (reset) {
      setLoading(true);
        setPage(1);
        setHasMore(true);
        setLastId(null);
      } else {
        setLoadingMore(true);
      }
      
      // authServiceと同じキーを使用
      const token = localStorage.getItem('authToken');
      
      // APIパラメータを設定
      const params = { 
        limit: 5, // 5件に変更
        page: reset ? 1 : page + 1,
        exclude_advertisements: true 
      };
      
      // 追加読み込みの場合はlastIdを使用
      if (!reset && lastId) {
        params.before_id = lastId;
      }
      
      const response = await apiService.get(`/api/posts/user/${username}/replies`, {
        params,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.replies) {
        const newReplies = response.data.replies;
      
        if (reset) {
          setReplies(newReplies);
        } else {
          setReplies(prevReplies => [...prevReplies, ...newReplies]);
        }
      
        // hasMoreの判定
        if (newReplies.length < params.limit) {
          setHasMore(false);
      } else {
          setHasMore(true);
        }
        
        // 最後のIDを設定
        if (newReplies.length > 0) {
          setLastId(newReplies[newReplies.length - 1].id);
          if (!reset) {
            setPage(prev => prev + 1);
          }
        }
      } else {
        
        if (reset) {
        setReplies([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      
      if (reset) {
      setReplies([]);
      }
      setHasMore(false);
    } finally {
      if (reset) {
      setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };
  
  const fetchUserMediaPosts = async (reset = true) => {
    try {
      console.log('=== メディア投稿取得開始 ===', { reset });
      
      if (reset) {
        setLoading(true);
        setMediaPosts([]); // キャッシュクリア
      }
      
      // authServiceと同じキーを使用
      const token = localStorage.getItem('authToken');
      
      const response = await apiService.get(`/api/posts/user/${username}/media`, {
        params: { 
          limit: 5, 
          page: 1,
          exclude_advertisements: true,
          _cache_bust: Date.now() // キャッシュ回避
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.posts) {
        // クライアント側でも再度メディア投稿のみをフィルタリング
        const filteredPosts = response.data.posts.filter(post => 
          post.image || post.video || 
          (post.media && post.media.length > 0) || 
          post.image_url
        );
        
        console.log('メディア投稿取得完了:', { 
          totalPosts: response.data.posts.length, 
          filteredPosts: filteredPosts.length 
        });
        
        setMediaPosts(filteredPosts);
      } else {
        console.log('メディア投稿データなし');
        setMediaPosts([]);
      }
    } catch (error) {
      console.error('メディア投稿取得エラー:', error);
      setMediaPosts([]);
    } finally {
      if (reset) {
        setLoading(false);
      }
    }
  };
  
  const fetchUserLikedPosts = async (reset = true) => {
    try {
      if (reset) {
      setLoading(true);
        setPage(1);
        setHasMore(true);
        setLastId(null);
      } else {
        setLoadingMore(true);
      }
      
      // authServiceと同じキーを使用
      const token = localStorage.getItem('authToken');
      
      // APIパラメータを設定
      const params = { 
        limit: 5, // 5件に変更
        page: reset ? 1 : page + 1,
        exclude_advertisements: true 
      };
      
      // 追加読み込みの場合はlastIdを使用
      if (!reset && lastId) {
        params.before_id = lastId;
      }
      
      const response = await apiService.get(`/api/posts/user/${username}/likes`, {
        params,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.posts) {
        const newLikedPosts = response.data.posts;
      
        if (reset) {
          setLikedPosts(newLikedPosts);
        } else {
          setLikedPosts(prevLikedPosts => {
            // 重複チェック：既存の投稿IDを抽出
            const existingIds = new Set(prevLikedPosts.map(post => post.id));
            // 新しい投稿のみを追加（重複を排除）
            const uniqueNewPosts = newLikedPosts.filter(post => !existingIds.has(post.id));
            console.log(`いいね投稿重複チェック: 既存${prevLikedPosts.length}件、新規${newLikedPosts.length}件、重複除外後${uniqueNewPosts.length}件`);
            return [...prevLikedPosts, ...uniqueNewPosts];
          });
        }
      
        // hasMoreの判定
        if (newLikedPosts.length < params.limit) {
          setHasMore(false);
      } else {
          setHasMore(true);
        }
        
        // 最後のIDを設定
        if (newLikedPosts.length > 0) {
          setLastId(newLikedPosts[newLikedPosts.length - 1].id);
          if (!reset) {
            setPage(prev => prev + 1);
          }
        }
      } else {
        
        if (reset) {
        setLikedPosts([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      
      if (reset) {
      setLikedPosts([]);
      }
      setHasMore(false);
    } finally {
      if (reset) {
      setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };
  
  const fetchUserLists = async (reset = true) => {
    try {
      console.log('=== ユーザーリスト取得開始 ===', { reset });
      
      if (reset) {
        setLoading(true);
        setPublicLists([]); // キャッシュクリア
      }
      
      const token = localStorage.getItem('token');
      const response = await apiService.get(`/api/lists/user/${username}/public`, {
        params: {
          _cache_bust: Date.now() // キャッシュ回避
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.lists) {
        // APIから返されたリストデータを加工してメンバー数を確実に持つようにする
        const listsWithMemberCount = response.data.lists.map(list => ({
          ...list,
          member_count: list.member_count || list.members_count || list.membersCount || 0
        }));
        
        console.log('ユーザーリスト取得完了:', listsWithMemberCount.length);
        setPublicLists(listsWithMemberCount);
      } else {
        console.log('ユーザーリストデータなし');
        setPublicLists([]);
      }
    } catch (error) {
      console.error('ユーザーリスト取得エラー:', error);
      setPublicLists([]);
    } finally {
      if (reset) {
        setLoading(false);
      }
    }
  };

  // スクロール位置を監視して、80%スクロールしたときに次のコンテンツを読み込む
  useEffect(() => {
    let timeoutId = null;

    // スクロールイベントハンドラ
    const handleScroll = () => {
      // 無限スクロールをサポートするタブのみ監視
      if (!['posts', 'media', 'replies', 'likes'].includes(activeTab)) return;
      
      // 既存のタイマーをクリア（スロットリング）
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        if (loadingMore || !hasMore) return;
        
        // スクロール位置の計算
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        
        // スクロール位置が60%に達したかチェック
        const scrollPercentage = (scrollTop + windowHeight) / documentHeight;
        
        if (scrollPercentage >= 0.6) {
          console.log(`スクロール60%に到達 - ${activeTab}タブで追加読み込み`);
          loadMorePosts();
        }
      }, 100); // 100msのスロットリング
    };
    
    // スクロールイベントリスナーを追加
    window.addEventListener('scroll', handleScroll);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loadingMore, hasMore, activeTab, loadMorePosts]);

  // ユーザー自身のプロフィール表示時に統計情報を取得して表示するセクションを追加
  const UserStats = ({ username, userId, isCurrentUser }) => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [showStats, setShowStats] = useState(false);

    const fetchUserStats = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${config.API_BASE_URL}/api/stats/user/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.data.status === 'success') {
          setStats(response.data.data);
          setError(null);
        } else {
          setError('統計データの取得に失敗しました');
        }
      } catch (err) {
        
        setError('統計データの取得中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (isCurrentUser && showStats) {
        fetchUserStats();
      }
    }, [userId, isCurrentUser, showStats]);

    if (!isCurrentUser) return null;

    const handleToggleStats = () => {
      setShowStats(!showStats);
    };

    return (
      <Box sx={{ mt: 3, mb: 2 }}>
        <Button 
          variant="outlined" 
          onClick={handleToggleStats}
          startIcon={showStats ? <VisibilityOffIcon /> : <VisibilityIcon />}
          sx={{ mb: 2 }}
        >
          {showStats ? '統計を隠す' : '統計を表示'}
        </Button>

        {showStats && (
          <Paper sx={{ p: 2, mt: 1 }}>
            <Typography variant="h6" gutterBottom>あなたの統計情報</Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            ) : stats ? (
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="投稿" 
                    value={stats.engagement.posts} 
                    icon={<PostAddIcon color="primary" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="いいね獲得" 
                    value={stats.engagement.likes_received} 
                    icon={<FavoriteIcon color="error" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="プロフィール閲覧" 
                    value={stats.engagement.profile_views} 
                    icon={<PersonIcon color="secondary" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="投稿閲覧" 
                    value={stats.engagement.post_views} 
                    icon={<VisibilityIcon color="info" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="フォロワー" 
                    value={stats.engagement.followers} 
                    icon={<PeopleIcon color="success" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="フォロー中" 
                    value={stats.engagement.following} 
                    icon={<PersonAddIcon color="primary" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="コメント獲得" 
                    value={stats.engagement.comments_received} 
                    icon={<ChatIcon color="warning" />} 
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <StatItem 
                    title="ブックマーク獲得" 
                    value={stats.engagement.bookmarks_received} 
                    icon={<BookmarkIcon color="success" />} 
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography color="text.secondary">データがありません</Typography>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  // アバターモーダルのスタイル
  const modalStyle = {    position: 'absolute',    top: '50%',    left: '50%',    transform: 'translate(-50%, -50%)',    bgcolor: 'background.paper',    boxShadow: 24,    p: 1,    outline: 'none',    borderRadius: '10px',    maxWidth: '90vw',    maxHeight: '90vh',    display: 'flex',    flexDirection: 'column',    justifyContent: 'center',    alignItems: 'center'  };

  // フォロー中/フォロワーページにアクセスできるかのチェック関数
  const canAccessFollowingList = () => {
    // 自分自身のページか管理者の場合はアクセス可
    return isCurrentUserProfile || (currentUser && currentUser.role === 'admin');
  };

  const canAccessFollowersList = () => {
    // ゲスト以外はフォロワーリストにアクセス可能
    return currentUser && currentUser.role !== 'guest';
  };

  // フォロー状態の切り替え処理
  const handleFollowToggle = async () => {
    if (!isLoggedIn) {
      // 未ログインの場合はメッセージ表示（ゲストアクセス対応）
      alert('フォロー機能を利用するには会員登録またはログインが必要です。');
      return;
    }
    
    try {
      setIsFollowingLoading(true);
      
      // フォロー/アンフォローのエンドポイントを決定
      const endpoint = isFollowing
        ? `/api/social/unfollow/${username}`
        : `/api/social/follow/${username}`;
        
      
      
      // safeApiCallを使用してフォロー状態を切り替え
      await safeApiCall.post(endpoint);
      
      try {
        // フォロー状態更新後にフォローリストを再取得
        const followingEndpoint = `/api/social/following/${currentUser.username}`;
        const followingResponse = await safeApiCall.get(followingEndpoint, {
          useCache: false // キャッシュを使わず最新の情報を取得
        });
        
        // データ構造に応じてフォローリストを取得
        let followingList = [];
        if (followingResponse.data) {
          if (Array.isArray(followingResponse.data)) {
            followingList = followingResponse.data;
          } else if (followingResponse.data.users && Array.isArray(followingResponse.data.users)) {
            followingList = followingResponse.data.users;
          } else if (followingResponse.data.following && Array.isArray(followingResponse.data.following)) {
            followingList = followingResponse.data.following;
          }
        }
        
        // フォロー状態を更新
        const followCheckResponse = await safeApiCall.get(`/api/social/is_following/${username}`, {
          useCache: false
        });
        
        if (followCheckResponse && followCheckResponse.data) {
          setIsFollowing(followCheckResponse.data.is_following === true);
          
        } else {
          // APIからの確認が取れない場合は状態を反転
          setIsFollowing(!isFollowing);
        }
        
        // ユーザー名が正規表現に含まれている場合、そのプロフィールを更新
        if (username === profile.username) {
          
          fetchUserPosts(username); // 投稿を再取得
        }
      } catch (error) {
        
        // エラーが発生した場合でも状態を反転
        setIsFollowing(!isFollowing);
      }
      
      // フォロー数/フォロワー数も更新
      fetchActualFollowCounts(username);
      
    } catch (error) {
      
      
      let errorMsg = '操作に失敗しました';
      if (error.response && error.response.data && error.response.data.error) {
        errorMsg = error.response.data.error;
      }
      
      // エラーメッセージをユーザーに表示
      alert(errorMsg);
    } finally {
      setIsFollowingLoading(false);
    }
  };

  // ユーザープロフィール情報を再取得する関数
  const refreshUserProfile = async () => {
    try {
      setLoading(true);
      
      // トークンを取得
      const token = localStorage.getItem('token');
      if (!token && !isPublicProfile(username)) {
        
      }
      
      // プロフィールを再取得
      const profileResponse = await apiService.get(`/api/users/${username}`, {
        method: 'get', // 明示的にHTTPメソッドを指定
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // デバッグ用：レスポンスの詳細をログ出力
      
      
      // HTMLレスポンスをチェック（緩和した条件）
      if (profileResponse.data && typeof profileResponse.data === 'string' && 
          profileResponse.data.includes('<!DOCTYPE html>') && 
          profileResponse.data.includes('<html')) {
        
        throw new Error('APIからHTMLが返されました。認証問題または権限の問題かもしれません。');
      }
      
      // レスポンスデータがnullやundefinedの場合にも明示的にエラーを投げる
      if (!profileResponse.data) {
        
        throw new Error('プロフィールデータを取得できませんでした。');
      }
      
      // レスポンスデータの構造を確認
      
      
      setProfile(profileResponse.data);
      
      // プロフィールデータのデバッグ情報を出力
      
      
      
      // 投稿ユーザープロフィールの検索
      let posterProfileData = null;
      if (profileResponse.data.poster_profile) {
        posterProfileData = profileResponse.data.poster_profile;
      } else if (profileResponse.data.posterProfile) {
        posterProfileData = profileResponse.data.posterProfile;
      }
      
      
      // 企業アカウント情報をデバッグ出力
      if (posterProfileData) {
        
        
        // 源氏名（stage_name）のデバッグ出力
        
      }
      
      // poster_profileをセット（API応答形式の違いに対応）
      if (posterProfileData && !profileResponse.data.poster_profile) {
        profileResponse.data.poster_profile = posterProfileData;
      }
      
      // 投稿を再取得
      const postsResponse = await apiService.get(`/api/posts/user/${username}`, {
        params: { limit: 5, page: 1 },
        method: 'get', // 明示的にHTTPメソッドを指定
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // デバッグ用：投稿レスポンスの詳細をログ出力
      
      
      // HTMLレスポンスをチェック（緩和した条件）
      if (postsResponse.data && typeof postsResponse.data === 'string' && 
          postsResponse.data.includes('<!DOCTYPE html>') && 
          postsResponse.data.includes('<html')) {
        
        throw new Error('投稿APIからHTMLが返されました。認証問題または権限の問題かもしれません。');
      }
      
      // postsResponseがない場合のセーフガード
      if (!postsResponse.data) {
        
        // 空の投稿リストを設定（エラーではなく空の状態として処理）
        setPosts([]);
        setMediaPosts([]);
        setHasMore(false);
      } else {
        // 投稿データを設定（posts配列がない場合は空配列を使用）
        const postsList = postsResponse.data.posts || [];
        setPosts(postsList);
        
        // 最後の投稿IDを保存して、次の取得に使用
        if (postsList.length > 0) {
          setLastId(postsList[postsList.length - 1].id);
        }
        
        // メディア投稿を抽出（画像があるもののみ）
        const mediaOnly = postsResponse.data.posts.filter(post => post && post.image);
        setMediaPosts(mediaOnly);
      }
      
      // 公開リストを取得
      try {
        
        const listsResponse = await apiService.get(`/api/lists/user/${username}`, {
          method: 'get', // 明示的にHTTPメソッドを指定
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          params: {
            _t: Date.now() // キャッシュ回避のためのタイムスタンプ
          }
        });
        
        // HTMLレスポンスをチェック
        if (listsResponse.data && typeof listsResponse.data === 'string' && 
            listsResponse.data.includes('<!DOCTYPE html>')) {
          
          throw new Error('公開リストAPIからHTMLが返されました。');
        }
        
        
        
        if (listsResponse.data && listsResponse.data.lists) {
          
          setPublicLists(listsResponse.data.lists);
        } else {
          
          setPublicLists([]);
        }
      } catch (listsError) {
        
        setPublicLists([]);
      }
      
      // フォロー中・フォロワー数を設定
      if (profileResponse.data) {
        
        
        // 利用可能なフィールドに応じてカウントを設定
        setFollowingCount(
          profileResponse.data.following_count || 
          profileResponse.data.following || 
          0
        );
        setFollowersCount(
          profileResponse.data.followers_count || 
          profileResponse.data.followers || 
          0
        );
        
        // 実際のフォロー数とフォロワー数を取得（更新時にも正確な情報を取得）
        await fetchActualFollowCounts(username);
        
        // フォロー状態を確認 - フォローリストから正確な情報を取得
        if (token && isLoggedIn && currentUser && currentUser.username !== username) {
          try {
            // フォローリスト取得による正確なフォロー状態確認
            const followingEndpoint = `/api/social/following/${currentUser.username}`;
            const followingResponse = await apiService.get(followingEndpoint, {
              method: 'get', // 明示的にHTTPメソッドを指定
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              params: {
                _t: Date.now() // キャッシュ回避のためのタイムスタンプ
              }
            });
            
            // レスポンスデータ構造のログ出力
            
            
            // データ構造に応じて適切にフォローリストを取得
            let followingList = [];
            if (followingResponse.data) {
              if (Array.isArray(followingResponse.data)) {
                followingList = followingResponse.data;
              } else if (followingResponse.data.users && Array.isArray(followingResponse.data.users)) {
                followingList = followingResponse.data.users;
              } else if (followingResponse.data.following && Array.isArray(followingResponse.data.following)) {
                followingList = followingResponse.data.following;
              }
            }
            
            
            
            // 配列確認後にフォロー状態チェック
            let isUserFollowed = false;
            if (Array.isArray(followingList)) {
              isUserFollowed = followingList.some(user => {
                if (typeof user === 'object' && user !== null) {
                  return user.username === username || 
                        (user.id && profileResponse.data && user.id === profileResponse.data.id);
                } else if (typeof user === 'string') {
                  return user === username;
                }
                return false;
              });
            }
            
            
            setIsFollowing(isUserFollowed);
          } catch (followErr) {
            
            
            // エラー時はAPIのis_followingを使うか、フォロー試行で確認
            if (typeof profileResponse.data.is_following === 'boolean') {
              
              setIsFollowing(profileResponse.data.is_following);
            } else {
              
              await tryFollowToCheckStatus(username, token);
            }
          }
        }
      }
      
      // 投稿データを設定
      if (postsResponse.data && postsResponse.data.posts) {
        // 投稿データを設定
        setPosts(postsResponse.data.posts);
        
        // 最後の投稿IDを保存して、次の取得に使用
        if (postsResponse.data.posts.length > 0) {
          setLastId(postsResponse.data.posts[postsResponse.data.posts.length - 1].id);
        }
        
        // メディア投稿を抽出（画像があるもののみ）
        const mediaOnly = postsResponse.data.posts.filter(post => post && post.image);
        setMediaPosts(mediaOnly);
      } else {
        setPosts([]);
        setMediaPosts([]);
        setHasMore(false);
      }
      
      // コメント/返信データを取得
      if (token) {
        try {
          // 返信データの取得を試みる
          // バックエンドに新しく実装したAPIを使用
          const repliesResponse = await apiService.get(`/api/posts/user/${username}/replies`, {
            method: 'get', // 明示的にHTTPメソッドを指定
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            params: {
              _t: Date.now() // キャッシュ回避のためのタイムスタンプ
            }
          });
          
          // HTMLレスポンスをチェック
          if (repliesResponse.data && typeof repliesResponse.data === 'string' && 
              repliesResponse.data.includes('<!DOCTYPE html>')) {
            
            throw new Error('返信APIからHTMLが返されました。');
          }
          
          if (repliesResponse.data && repliesResponse.data.replies) {
            setReplies(repliesResponse.data.replies);
            
          } else {
            // 返信データが空の場合、代替手段を使用
            if (postsResponse && postsResponse.data && postsResponse.data.posts) {
              const possibleReplies = postsResponse.data.posts.filter(post => 
                post.text && (post.text.startsWith('@') || post.text.includes(' @'))
              );
              setReplies(possibleReplies);
              
            } else {
              
              setReplies([]);
            }
          }
        } catch (replyError) {
          
          // エラーが発生した場合は投稿から@で始まる投稿を返信とみなす
          if (postsResponse && postsResponse.data && postsResponse.data.posts) {
            const possibleReplies = postsResponse.data.posts.filter(post => 
              post.text && (post.text.startsWith('@') || post.text.includes(' @'))
            );
            setReplies(possibleReplies);
          } else {
            
            setReplies([]);
          }
        }
        
        // いいねした投稿を取得
        try {
          // 新しく実装したいいね取得APIを使用
          const likesResponse = await apiService.get(`/api/posts/user/${username}/likes`, {
            method: 'get', // 明示的にHTTPメソッドを指定
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            params: {
              _t: Date.now() // キャッシュ回避のためのタイムスタンプ
            }
          });
          
          // HTMLレスポンスをチェック
          if (likesResponse.data && typeof likesResponse.data === 'string' && 
              likesResponse.data.includes('<!DOCTYPE html>')) {
            
            throw new Error('いいねAPIからHTMLが返されました。');
          }
          
          if (likesResponse.data && likesResponse.data.posts) {
            setLikedPosts(likesResponse.data.posts);
            
          } else {
            // データが無い場合は空の配列をセット
            setLikedPosts([]);
            
          }
        } catch (likesError) {
          
          // エラー時にはバックアップとしてブックマークを試す
          try {
            const bookmarksResponse = await apiService.get(`/api/posts/bookmarks`, {
              method: 'get', // 明示的にHTTPメソッドを指定
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              params: {
                _t: Date.now() // キャッシュ回避のためのタイムスタンプ
              }
            });
            
            // HTMLレスポンスをチェック
            if (bookmarksResponse.data && typeof bookmarksResponse.data === 'string' && 
                bookmarksResponse.data.includes('<!DOCTYPE html>')) {
              
              throw new Error('ブックマークAPIからHTMLが返されました。');
            }
            
            if (bookmarksResponse.data && bookmarksResponse.data.bookmarks) {
              setLikedPosts(bookmarksResponse.data.bookmarks);
              
            } else {
              setLikedPosts([]);
            }
          } catch (backupError) {
            
            setLikedPosts([]);
          }
        }
      }
    } catch (error) {
      
      setError('プロフィールの更新中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  // フォロー状態を試行エラーで確認する関数
  const tryFollowToCheckStatus = async (targetUsername, token) => {
    try {
      
      
      // トークンが必要であることを検証
      if (!token) {
        
        return false;
      }
      
      try {
        // safeApiCallを使用してフォローAPIを呼び出し
        await safeApiCall.post(`/api/social/follow/${targetUsername}`);
        
        return true;
      } catch (error) {
        // 409エラーは既にフォロー済みという意味
        if (error.response && error.response.status === 409) {
          
          return true;
        }
        
        return false;
      }
    } catch (error) {
      
      return false;
    }
  };

  // メニューを開く
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  // メニューを閉じる
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // 通報ページへ移動
  const handleReport = () => {
    handleMenuClose();
    navigate(`/report?user=${username}`);
  };

  // 統計項目を表示するコンポーネント
  const StatItem = ({ title, value, icon }) => (
    <Box 
      sx={{
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        margin: '0 8px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
    </Box>
  );

  // タブコンテンツを表示する関数
  const getTabContent = () => {
    switch (activeTab) {
      case 'posts':
        return (
          <>
            {posts.length === 0 ? (
              <div className="profile__noPosts">
                <Typography variant="body1" sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  投稿がまだありません
                </Typography>
              </div>
            ) : (
              posts.filter(post => !post.is_deleted).map(post => (
                <div 
                  key={post.is_retweet ? `rt_${post.id}_${post.retweeter?.id || 'unknown'}` : `post_${post.id}`}
                >
                  <Post
                    id={post.original_post_id || post.id}
                    displayName={
                      post.is_retweet && post.original_author 
                        ? post.original_author.display_name || post.original_author.displayName
                        : post.author?.display_name || post.author?.displayName || post.user?.display_name || post.user?.displayName || '未設定'
                    }
                    username={
                      post.is_retweet && post.original_author 
                        ? post.original_author.username
                        : post.author?.username || post.user?.username || '不明'
                    }
                    verified={
                      post.is_retweet && post.original_author
                        ? post.original_author.verified
                        : post.author?.verified || post.user?.verified || false
                    }
                    text={post.text}
                    avatar={
                      post.is_retweet && post.original_author
                        ? post.original_author.avatar
                        : post.author?.avatar || post.user?.avatar || ''
                    }
                    image={post.image || post.image_url}
                    video={post.video || post.video_url || ''}
                    poll={post.poll}
                    has_poll={post.has_poll || false}
                    shifts={post.shifts}
                    createdAt={post.created_at}
                    likes={post.likes_count}
                    comments={post.comments_count}
                    retweets={post.reposts_count || 0}
                    isRetweet={post.is_retweet}
                    retweeter={post.retweeter}
                    retweeted={post.retweeted || false}
                    authorId={
                      post.is_retweet && post.original_author
                        ? post.original_author.id
                        : post.author?.id || post.author_id || post.user?.id || 0
                    }
                    isProfilePage={true}
                    isPinned={post.is_pinned || false}
                    onComment={handleCommentPost}
                    // ユーザーアクション情報を追加（数値で統一して判定）
                    liked={userActions.liked_posts.some(id => Number(id) === Number(post.id))}
                    bookmarked={userActions.bookmarked_posts.some(id => Number(id) === Number(post.id))}
                    retweetedByUser={userActions.retweeted_posts.some(id => Number(id) === Number(post.id))}
                  />
                </div>
              ))
            )}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
              </Box>
            )}
            <div ref={loadMoreRef}></div>
          </>
        );
      case 'replies':
        return (
          <>
            {replies.length === 0 ? (
              <div className="profile__noPosts">
                <Typography variant="body1" sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  返信がまだありません
                </Typography>
              </div>
            ) : (
              replies.filter(post => !post.is_deleted).map(post => (
                <div 
                  key={post.is_retweet ? `rt_${post.id}_${post.retweeter?.id || 'unknown'}` : `post_${post.id}`}
                >
                  <Post
                    id={post.original_post_id || post.id}
                    displayName={
                      post.is_retweet && post.original_author 
                        ? post.original_author.display_name || post.original_author.displayName
                        : post.author?.display_name || post.author?.displayName || post.user?.display_name || post.user?.displayName || '未設定'
                    }
                    username={
                      post.is_retweet && post.original_author 
                        ? post.original_author.username
                        : post.author?.username || post.user?.username || '不明'
                    }
                    verified={
                      post.is_retweet && post.original_author
                        ? post.original_author.verified
                        : post.author?.verified || post.user?.verified || false
                    }
                    text={post.text}
                    avatar={
                      post.is_retweet && post.original_author
                        ? post.original_author.avatar
                        : post.author?.avatar || post.user?.avatar || ''
                    }
                    image={post.image || post.image_url}
                    video={post.video || post.video_url || ''}
                    poll={post.poll}
                    has_poll={post.has_poll || false}
                    shifts={post.shifts}
                    createdAt={post.created_at}
                    likes={post.likes_count}
                    comments={post.comments_count}
                    retweets={post.reposts_count || 0}
                    isRetweet={post.is_retweet}
                    retweeter={post.retweeter}
                    retweeted={post.retweeted || false}
                    authorId={
                      post.is_retweet && post.original_author
                        ? post.original_author.id
                        : post.author?.id || post.author_id || post.user?.id || 0
                    }
                    isProfilePage={true}
                    isPinned={post.is_pinned || false}
                    onComment={handleCommentPost}
                    // ユーザーアクション情報を追加（数値で統一して判定）
                    liked={userActions.liked_posts.some(id => Number(id) === Number(post.id))}
                    bookmarked={userActions.bookmarked_posts.some(id => Number(id) === Number(post.id))}
                    retweetedByUser={userActions.retweeted_posts.some(id => Number(id) === Number(post.id))}
                  />
                </div>
              ))
            )}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ ml: 1 }}>さらに読み込み中...</Typography>
              </Box>
            )}
            
            {/* これ以上投稿がない場合の表示 */}
            {!loading && !loadingMore && !hasMore && posts.length > 0 && (
              <Box sx={{ p: 2, my: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  これ以上投稿はありません
                </Typography>
              </Box>
            )}
          </>
        );
      case 'media':
        return (
          <>
            {mediaPosts.length === 0 ? (
              <div className="profile__noPosts">
                <Typography variant="body1" sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  メディア投稿がまだありません
                </Typography>
              </div>
            ) : (
              mediaPosts.filter(post => !post.is_deleted).map(post => (
                <div 
                  key={post.is_retweet ? `rt_${post.id}_${post.retweeter?.id || 'unknown'}` : `post_${post.id}`}
                >
                  <Post
                    id={post.original_post_id || post.id}
                    displayName={
                      post.is_retweet && post.original_author 
                        ? post.original_author.display_name || post.original_author.displayName
                        : post.author?.display_name || post.author?.displayName || post.user?.display_name || post.user?.displayName || '未設定'
                    }
                    username={
                      post.is_retweet && post.original_author 
                        ? post.original_author.username
                        : post.author?.username || post.user?.username || '不明'
                    }
                    verified={
                      post.is_retweet && post.original_author
                        ? post.original_author.verified
                        : post.author?.verified || post.user?.verified || false
                    }
                    text={post.text}
                    avatar={
                      post.is_retweet && post.original_author
                        ? post.original_author.avatar
                        : post.author?.avatar || post.user?.avatar || ''
                    }
                    image={post.image || post.image_url}
                    video={post.video || post.video_url || ''}
                    poll={post.poll}
                    has_poll={post.has_poll || false}
                    shifts={post.shifts}
                    createdAt={post.created_at}
                    likes={post.likes_count}
                    comments={post.comments_count}
                    retweets={post.reposts_count || 0}
                    isRetweet={post.is_retweet}
                    retweeter={post.retweeter}
                    retweeted={post.retweeted || false}
                    authorId={
                      post.is_retweet && post.original_author
                        ? post.original_author.id
                        : post.author?.id || post.author_id || post.user?.id || 0
                    }
                    isProfilePage={true}
                    isPinned={post.is_pinned || false}
                    onComment={handleCommentPost}
                    // ユーザーアクション情報を追加（数値で統一して判定）
                    liked={userActions.liked_posts.some(id => Number(id) === Number(post.id))}
                    bookmarked={userActions.bookmarked_posts.some(id => Number(id) === Number(post.id))}
                    retweetedByUser={userActions.retweeted_posts.some(id => Number(id) === Number(post.id))}
                  />
                </div>
              ))
            )}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ ml: 1 }}>さらに読み込み中...</Typography>
              </Box>
            )}
            
            {/* これ以上メディア投稿がない場合の表示 */}
            {!loading && !loadingMore && !hasMore && mediaPosts.length > 0 && (
              <Box sx={{ p: 2, my: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  これ以上メディア投稿はありません
                </Typography>
              </Box>
            )}
          </>
        );
      case 'likes':
        return (
          <>
            {likedPosts.length === 0 ? (
              <div className="profile__noPosts">
                <Typography variant="body1" sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  いいねした投稿がまだありません
                </Typography>
              </div>
            ) : (
              likedPosts.filter(post => !post.is_deleted).map(post => (
                <div 
                  key={post.is_retweet ? `rt_${post.id}_${post.retweeter?.id || 'unknown'}` : `post_${post.id}`}
                >
                  <Post
                    id={post.original_post_id || post.id}
                    displayName={
                      post.is_retweet && post.original_author 
                        ? post.original_author.display_name || post.original_author.displayName
                        : post.author?.display_name || post.author?.displayName || post.user?.display_name || post.user?.displayName || '未設定'
                    }
                    username={
                      post.is_retweet && post.original_author 
                        ? post.original_author.username
                        : post.author?.username || post.user?.username || '不明'
                    }
                    verified={
                      post.is_retweet && post.original_author
                        ? post.original_author.verified
                        : post.author?.verified || post.user?.verified || false
                    }
                    text={post.text}
                    avatar={
                      post.is_retweet && post.original_author
                        ? post.original_author.avatar
                        : post.author?.avatar || post.user?.avatar || ''
                    }
                    image={post.image || post.image_url}
                    video={post.video || post.video_url || ''}
                    poll={post.poll}
                    has_poll={post.has_poll || false}
                    shifts={post.shifts}
                    createdAt={post.created_at}
                    likes={post.likes_count}
                    comments={post.comments_count}
                    retweets={post.reposts_count || 0}
                    isRetweet={post.is_retweet}
                    retweeter={post.retweeter}
                    retweeted={post.retweeted || false}
                    authorId={
                      post.is_retweet && post.original_author
                        ? post.original_author.id
                        : post.author?.id || post.author_id || post.user?.id || 0
                    }
                    isProfilePage={true}
                    isPinned={post.is_pinned || false}
                    onComment={handleCommentPost}
                    // ユーザーアクション情報を追加（数値で統一して判定）
                    liked={userActions.liked_posts.some(id => Number(id) === Number(post.id))}
                    bookmarked={userActions.bookmarked_posts.some(id => Number(id) === Number(post.id))}
                    retweetedByUser={userActions.retweeted_posts.some(id => Number(id) === Number(post.id))}
                  />
                </div>
              ))
            )}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ ml: 1 }}>さらに読み込み中...</Typography>
              </Box>
            )}
            
            {/* これ以上いいねした投稿がない場合の表示 */}
            {!loading && !loadingMore && !hasMore && likedPosts.length > 0 && (
              <Box sx={{ p: 2, my: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  これ以上いいねした投稿はありません
                </Typography>
              </Box>
            )}
          </>
        );
      case 'lists':
        return (
          <>
            {publicLists.length === 0 ? (
              <div className="profile__noPosts">
                <Typography variant="body1" sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  リストがまだありません
                </Typography>
              </div>
            ) : (
              <List>
                {publicLists.map(list => (
                  <ListItem 
                    key={list.id} 
                    button 
                    component={Link} 
                    to={`/lists/${list.id}`}
                    sx={{ mb: 1, borderRadius: 1, border: '1px solid #eee' }}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <FormatListBulletedIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={list.name} 
                      secondary={
                        <>
                          {list.description && (
                            <Typography variant="body2" component="span" display="block" color="text.secondary">
                              {list.description}
                            </Typography>
                          )}
                          <Typography variant="caption" component="span" color="text.secondary">
                            {list.is_private ? 'プライベート' : '公開'} • {list.member_count || 0}人のメンバー
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        );
      case 'reviews':
        return (
          <>
            {/* クチコミタブのコンテンツ */}
            {profile && (
              <ReviewsTab
                targetId={profile.id}
                targetType={profile.role === 'corporate' ? 'corporate' : 'poster'}
                currentUserRole={currentUser?.role}
                currentUserId={currentUser?.uid}
                isOwner={isCurrentUserProfile}
                isTaggedCorp={
                  profile.role === 'poster' &&
                  currentUser?.role === 'corporate' &&
                  profile.tagged_corporate &&
                  profile.tagged_corporate.includes(currentUser?.uid)
                }
              />
            )}
          </>
        );
      case 'shifts':
        return (
          <ShiftCalendar
            userId={profile?.id}
            username={username}
            displayName={profile?.display_name || profile?.displayName || profile?.stage_name || username}
            avatar={profile?.avatar || profile?.profile?.avatar}
          />
        );
      case 'reservations':
        return (
          <>
            {/* 予約タブのコンテンツ */}
            <Box sx={{ p: 2 }}>
              {isCurrentUserProfile && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAddIcon />}
                  onClick={handleCreateReservation}
                  sx={{ mb: 3 }}
                >
                  新しい予約を作成
                </Button>
              )}
              
              {reservationLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : reservations.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    予約がまだありません
                </Typography>
                </Box>
              ) : (
                <List>
                  {reservations.map(reservation => (
                    <Paper key={reservation.id} sx={{ mb: 2, p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Link to={`/profile/${reservation.poster_username}`} style={{ textDecoration: 'none' }}>
                              <Avatar
                                src={normalizeImageUrl(reservation.poster_avatar)}
                                alt={reservation.poster_display_name}
                                sx={{ 
                                  width: 50, 
                                  height: 50, 
                                  mr: 2,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    boxShadow: '0 0 0 2px #1976d2'
                                  }
                                }}
                              />
                            </Link>
                            <Box>
                              <Link to={`/profile/${reservation.poster_username}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#1976d2'
                                    } 
                                  }}
                                >
                                  {reservation.poster_display_name}
                                </Typography>
                              </Link>
                              <Typography variant="body2" color="text.secondary">
                                @{reservation.poster_username}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip
                                label={reservation.status_label || '未対応'}
                                size="small"
                                color={
                                  reservation.status === 'confirmed' ? 'success'
                                  : reservation.status === 'in_progress' ? 'info'
                                  : reservation.status === 'cancelled' ? 'error'
                                  : 'warning'
                                }
                              />
                            </Box>
                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                              <CalendarMonthIcon sx={{ mr: 1 }} />
                              {reservation.reservation_date}
                            </Typography>
                            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                              <ScheduleIcon sx={{ mr: 1 }} />
                              {reservation.start_time}
                              {reservation.end_time && ` ～ ${reservation.end_time}`}
                              {reservation.course_minutes && `（${reservation.course_minutes}分コース）`}
                            </Typography>
                            {reservation.hotel && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                ホテル（場所）: {reservation.hotel}
                              </Typography>
                            )}
                            {reservation.memo && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                その他要望: {reservation.memo}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/reservations/${reservation.id}`)}
                          >
                            詳細を見る
                          </Button>
                          {reservation.cancel_requested ? (
                            <Chip label="キャンセル依頼中" color="warning" size="small" />
                          ) : reservation.status !== 'cancelled' && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleCancelRequestReservation(reservation.id)}
                            >
                              キャンセル依頼
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </List>
              )}
            </Box>
            
            {/* 予約作成ダイアログ */}
            <Dialog open={openReservationDialog} onClose={handleCloseReservationDialog} maxWidth="sm" fullWidth>
              <DialogTitle>新しい予約を作成</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  フォロー中の投稿ユーザーから選択して予約を作成できます。
                </DialogContentText>
                
                <TextField
                  fullWidth
                  label="投稿ユーザーを検索"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchFollowingPosters(e.target.value);
                  }}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                  <List>
                    {/* シフト等からプリセットされた対象ユーザーが候補リストに無い場合は先頭に表示 */}
                    {selectedPoster && !followingPosters.some(p => p.id === selectedPoster.id) && (
                      <ListItem
                        key={selectedPoster.id}
                        button
                        onClick={() => handlePosterSelect(selectedPoster)}
                        selected
                      >
                        <ListItemAvatar>
                          <Avatar src={normalizeImageUrl(selectedPoster.avatar)} alt={selectedPoster.display_name} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={selectedPoster.display_name}
                          secondary={selectedPoster.username ? `@${selectedPoster.username}` : ''}
                        />
                      </ListItem>
                    )}
                    {followingPosters.map(poster => (
                      <ListItem
                        key={poster.id}
                        button
                        onClick={() => handlePosterSelect(poster)}
                        selected={selectedPoster && selectedPoster.id === poster.id}
                      >
                        <ListItemAvatar>
                          <Avatar src={normalizeImageUrl(poster.avatar)} alt={poster.display_name} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={poster.display_name}
                          secondary={`@${poster.username}`}
                        />
                      </ListItem>
                    ))}
                    {followingPosters.length === 0 && !selectedPoster && (
                      <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        フォロー中の投稿ユーザーが見つかりません
                      </Typography>
                    )}
                  </List>
                </Box>
                
                {selectedPoster && (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      選択中のユーザー: {selectedPoster.username || selectedPoster.display_name}
                      {getSelectedPosterShiftLabel() && `（${getSelectedPosterShiftLabel()}）`}
                    </Typography>
                    {/* 選択日のシフトが無い場合は注意表示 */}
                    {!getSelectedPosterShiftLabel() && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        選択した日は出勤シフトがありません。別の日を選択してください。
                      </Typography>
                    )}
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={ja}>
                          <DatePicker
                            label="予約日"
                            value={reservationDate}
                            onChange={(newDate) => setReservationDate(newDate)}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                            disablePast
                            inputFormat="yyyy/MM/dd"
                            format="yyyy/MM/dd"
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="開始時間"
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 600 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel id="course-select-label">コース</InputLabel>
                          <Select
                            labelId="course-select-label"
                            label="コース"
                            value={courseMinutes}
                            onChange={(e) => setCourseMinutes(Number(e.target.value))}
                          >
                            {Array.from({ length: 34 }, (_, i) => 30 + i * 10).map((min) => (
                              <MenuItem key={min} value={min}>
                                {min}分
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          終了予定時間: {startTime ? computeEndTime(startTime, courseMinutes) : '—'}（開始 + {courseMinutes}分）
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="ホテル（場所）"
                          value={hotel}
                          onChange={(e) => setHotel(e.target.value)}
                          placeholder="例: 〇〇ホテル 101号室 など"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="その他要望"
                          multiline
                          rows={3}
                          value={memo}
                          onChange={(e) => setMemo(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      ※ 仮予約は対象ユーザーの出勤シフトに含まれる日時のみ作成できます。作成後、対象ユーザーと関連企業に通知が送信されます。
                    </Typography>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseReservationDialog}>キャンセル</Button>
                <Button
                  onClick={handleCreateReservation}
                  variant="contained"
                  color="primary"
                  disabled={!selectedPoster || !reservationDate || !startTime}
                >
                  仮予約をする
                </Button>
              </DialogActions>
            </Dialog>
          </>
        );
      default:
        return null;
    }
  };

  // ユーザーの投稿を取得する関数（useCallbackでメモ化）
  const fetchUserPosts = useCallback(async (targetUsername, reset = true) => {
    if (!targetUsername) return;
    
    try {
      if (reset) {
      setLoading(true);
        setPage(1);
        setHasMore(true);
        setLastId(null);
      } else {
        setLoadingMore(true);
      }
      
      // APIパラメータを設定
      const params = { 
        page: reset ? 1 : page + 1,
        per_page: 5, // 5件に変更してパフォーマンス向上
          exclude_advertisements: true
      };
      
      // 追加読み込みの場合はlastIdを使用
      if (!reset && lastId) {
        params.before_id = lastId;
        }
      
      // PostDetail.jsと同じ方式でAPIからユーザーの投稿を取得
      const response = await guestSafeApiCall(`/api/posts/user/${targetUsername}`, {}, params);

              if (response.data && response.data.posts) {
          
          const newPosts = response.data.posts;
          
          if (reset) {
            setPosts(newPosts);
            // 初回取得時に総投稿数を設定
            if (response.data.total_count !== undefined) {
              setTotalPostsCount(response.data.total_count);
            }
          } else {
            setPosts(prevPosts => {
              // 重複チェック：既存の投稿IDを抽出
              const existingIds = new Set(prevPosts.map(post => post.id));
              // 新しい投稿のみを追加
              const uniqueNewPosts = newPosts.filter(post => !existingIds.has(post.id));
              return [...prevPosts, ...uniqueNewPosts];
            });
          }
          
          // hasMoreの判定
          if (newPosts.length < params.per_page) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
          
          // 最後の投稿IDを設定
          if (newPosts.length > 0) {
            setLastId(newPosts[newPosts.length - 1].id);
            if (!reset) {
              setPage(prev => prev + 1);
            }
          }
        
        // メディア投稿を更新（画像があるもののみ）
        if (activeTab === 'media') {
          const mediaOnly = newPosts.filter(post => post.image);
          if (reset) {
          setMediaPosts(mediaOnly);
          } else {
            setMediaPosts(prevMedia => [...prevMedia, ...mediaOnly]);
          }
        }
      } else {
        
        if (reset) {
        setPosts([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      
      if (reset) {
        setPosts([]);
      }
      setHasMore(false);
    } finally {
      if (reset) {
      setLoading(false);
      } else {
        setLoadingMore(false);
    }
    }
  }, [activeTab, page, lastId]);

  // リツイート状態変更のイベントハンドラ
  useEffect(() => {
    const handleRetweetUpdate = (event) => {
      // イベント詳細を取得
      const { postId, retweeted, author } = event.detail;
      
      
      
      if (retweeted) {
        // リツイート追加の場合、投稿のユーザーが現在表示中のプロファイルユーザーと同じなら更新
        if (author && author.username === username) {
          
          fetchUserPosts(username); // 投稿を再取得
        }
      } else {
        // リツイート解除の場合は、投稿の状態だけを更新
        setPosts(prevPosts => 
          prevPosts.map(post => {
            if (post.id === postId) {
              return {
                ...post,
                retweeted: false,
                is_retweeted: false,
                retweets_count: Math.max(0, (post.retweets_count || 0) - 1),
                retweet_count: Math.max(0, (post.retweet_count || 0) - 1)
              };
            }
            return post;
          })
        );
      }
    };

    window.addEventListener('retweet_update', handleRetweetUpdate);

    return () => {
      window.removeEventListener('retweet_update', handleRetweetUpdate);
    };
  }, [username]);

  // フォローとフォロワーの実際の数を取得する関数
  const fetchActualFollowCounts = async (targetUsername) => {
    try {
      // authServiceと同じキーを使用
      const token = localStorage.getItem('authToken');
      
      // フォロー中の数を取得（PostDetail.jsと同じ方式で直接axios使用）
      const headers = token ? { 
        Authorization: `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      } : {};
      
      const followingResponse = await axios.get(`${config.API_BASE_URL}/api/social/following/${targetUsername}`, {
        headers,
        params: { 
          page: 1, 
          per_page: 1,
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        }
      });

      // フォロワーの数を取得（PostDetail.jsと同じ方式で直接axios使用）
      const followersResponse = await axios.get(`${config.API_BASE_URL}/api/social/followers/${targetUsername}`, {
        headers,
        params: { 
          page: 1, 
          per_page: 1,
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        }
      });

      // フォロー数を更新（APIレスポンス形式に合わせて修正）
      setFollowingCount(followingResponse.data.total_count || followingResponse.data.total || 0);
      setFollowersCount(followersResponse.data.total_count || followersResponse.data.total || 0);
    } catch (error) {
      console.error('フォロー数取得エラー:', error);
      
      // エラーの場合はプロフィールのデフォルト値を使用
      setFollowingCount(profile?.following_count || 0);
      setFollowersCount(profile?.followers_count || 0);
    }
  };

  // ユーザーをブロックする処理
  const handleBlockUser = async () => {
    if (!isLoggedIn || !currentUser) return;
    
    try {
      setBlockLoading(true);
      handleMenuClose();
      
      // ユーザーのIDを取得
      const userId = profile?.id;
      if (!userId) {
        
        return;
      }
      
      // APIを呼び出してユーザーをブロック
      const response = await apiService.post('/api/users/block_user', {
        user_id: userId
      });
      
      if (response.status === 200) {
        setIsBlocked(true);
        // ブロック成功のアラートを表示
        alert('ユーザーをブロックしました');
      }
    } catch (error) {
      
      alert('ユーザーのブロックに失敗しました');
    } finally {
      setBlockLoading(false);
    }
  };
  
  // ブロック解除処理
  const handleUnblockUser = async () => {
    if (!isLoggedIn || !currentUser) return;
    
    try {
      setBlockLoading(true);
      handleMenuClose();
      
      // ユーザーのIDを取得
      const userId = profile?.id;
      if (!userId) {
        
        return;
      }
      
      // APIを呼び出してブロック解除
      const response = await apiService.post('/api/users/unblock_user', {
        user_id: userId
      });
      
      if (response.status === 200) {
        setIsBlocked(false);
        // ブロック解除成功のアラートを表示
        alert('ブロックを解除しました');
      }
    } catch (error) {
      
      alert('ブロック解除に失敗しました');
    } finally {
      setBlockLoading(false);
    }
  };

  // このユーザーがブロック可能かどうかを判定
  // 閲覧ユーザー、ゲストユーザー以外のユーザーのみブロック可能
  const canBlockUser = () => {
    return isLoggedIn && currentUser && 
           !isCurrentUserProfile && 
           currentUser.role !== 'guest' && 
           currentUser.role !== 'viewer';
  };

  // 企業プロフィール情報を表示するコンポーネント
  const CorporateProfileSection = ({ userId, parentProfile, username }) => {
    const [corporateData, setCorporateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCorporate, setIsCorporate] = useState(false);
    const { token } = useAuth();
    
    // 企業プロフィール情報を取得
    useEffect(() => {
      const fetchCorporateProfile = async () => {
        try {
          setLoading(true);
          
          // 既にparentProfileに企業情報が含まれている場合はそれを使用
          if (parentProfile && parentProfile.corporateProfile) {
            
            setCorporateData(parentProfile.corporateProfile);
            setIsCorporate(true);
            setLoading(false);
            return;
          }
          
          // APIから企業プロフィール情報を取得
          const response = await apiService.get(`/api/users/${username}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            },
            withCredentials: true, // 認証情報を含める
            params: {
              _t: Date.now() // キャッシュバスティング用パラメータを追加
            }
          });
          
          // レスポンスから企業情報を抽出
          if (response.data && response.data.role === 'corporate' && response.data.corporateProfile) {
            
            setCorporateData(response.data.corporateProfile);
            setIsCorporate(true);
          } else {
            
            setIsCorporate(false);
          }
          
        } catch (err) {
          
          setError('企業プロフィール情報の取得に失敗しました');
        } finally {
          setLoading(false);
        }
      };
      
      if (userId && username) {
        fetchCorporateProfile();
      }
    }, [userId, username, token, parentProfile]);
    
    // ローディング中
    if (loading) {
      return (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    // エラー表示
    if (error) {
      return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }
    
    // 企業ユーザーでない場合や情報がない場合は何も表示しない
    if (!isCorporate || !corporateData) {
      return null;
    }
    
    // 営業時間のフォーマット
    const formatBusinessHours = () => {
      const start = corporateData.businessHours?.start || '設定なし';
      const end = corporateData.businessHours?.end || '設定なし';
      return `${start} 〜 ${end}`;
    };
    
    // 業種タグの表示
    const renderIndustry = () => {
      if (!corporateData.industry || corporateData.industry.length === 0) {
        return '未設定';
      }
      
      return (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {corporateData.industry.map((ind, index) => (
            <Chip key={index} label={ind} color="primary" variant="outlined" size="small" />
          ))}
        </Box>
      );
    };
    
    return (
      <Paper className="corporate-profile-section" sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 1,
          mb: 2
        }}>
          <BusinessIcon sx={{ mr: 1 }} />
          企業情報
        </Typography>
        
        <Grid container spacing={2}>
          {/* 企業名 */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">
              {corporateData.corporateName || '未設定'}
            </Typography>
          </Grid>
          
          {/* 業種 */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">業種:</Typography>
              {renderIndustry()}
            </Box>
          </Grid>
          
          {/* 営業時間 */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon fontSize="small" sx={{ mr: 0.5 }} />
                営業時間:
              </Typography>
              <Typography variant="body2">
                {formatBusinessHours()}
              </Typography>
            </Box>
          </Grid>
          
          {/* 電話番号 */}
          {corporateData.phoneNumber && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} />
                  電話番号:
                </Typography>
                <Typography variant="body2">
                  <a 
                    href={`tel:${corporateData.phoneNumber}`}
                    style={{
                      color: '#1976d2',
                      fontWeight: 'bold',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                  {corporateData.phoneNumber}
                  </a>
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* 都道府県 */}
          {corporateData.prefecture && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                  都道府県:
                </Typography>
                <Typography variant="body2">
                  {corporateData.prefecture.name}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* 求人情報 */}
          {(corporateData.jobPostingUrl || corporateData.job_posting_url) && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <WorkIcon fontSize="small" sx={{ mr: 0.5 }} />
                  求人情報:
                </Typography>
                <a 
                  href={corporateData.jobPostingUrl || corporateData.job_posting_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    wordBreak: 'break-all',
                    color: '#1976d2',
                    fontWeight: 'bold',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  🔗 求人情報を見る
                </a>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  // 投稿ユーザープロフィール情報を表示するコンポーネント
  const PosterProfileSection = ({ userId, parentProfile, username }) => {
    const [posterData, setPosterData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPoster, setIsPoster] = useState(false);
    const { token } = useAuth();
    
    // 投稿ユーザープロフィール情報を取得
    useEffect(() => {
      const fetchPosterProfile = async () => {
        try {
          setLoading(true);
          
          // 既にparentProfileに投稿ユーザー情報が含まれている場合はそれを使用
          if (parentProfile && parentProfile.poster_profile) {
            
            
            setPosterData({
              ...parentProfile.poster_profile,
              avatar: parentProfile.avatar,
              cover_image: parentProfile.cover_image
            });
            setIsPoster(true);
            setLoading(false);
            return;
          }
          
          // APIからプロフィール情報を取得
          const response = await apiService.get(`/api/users/${username}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            },
            withCredentials: true, // 認証情報を含める
            params: {
              _t: Date.now() // キャッシュバスティング用パラメータを追加
            }
          });
          
          // レスポンスから投稿ユーザー情報を抽出
          if (response.data && response.data.role === 'poster' && response.data.poster_profile) {
            
            setPosterData(response.data.poster_profile);
            setIsPoster(true);
          } else {
            
            setIsPoster(false);
          }
          
        } catch (err) {
          
          setError('投稿ユーザープロフィール情報の取得に失敗しました');
        } finally {
          setLoading(false);
        }
      };
      
      if (userId && username) {
        fetchPosterProfile();
      }
    }, [userId, username, token, parentProfile]);
    
    // ローディング中
    if (loading) {
      return (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    // エラー表示
    if (error) {
      return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }
    
    // 投稿ユーザーでない場合や情報がない場合は何も表示しない
    if (!isPoster || !posterData) {
      return null;
    }
    
    return (
      <Paper className="poster-profile-section" sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 1,
          mb: 2
        }}>
          <PersonIcon sx={{ mr: 1 }} />
          投稿者情報
        </Typography>
        
        <Grid container spacing={2}>
          {/* 店舗名 */}
          {posterData.shop_name && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} />
                  所属店舗:
                </Typography>
                <Typography variant="body2">
                  {posterData.shop_name}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* 都道府県・市区町村 */}
          {(posterData.prefecture || posterData.city) && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                  エリア:
                </Typography>
                <Typography variant="body2">
                  {posterData.prefecture?.name || ''}
                  {posterData.city?.name && ` (${posterData.city.name})`}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* 芸名表示を削除 */}
          
          {/* 年齢・スリーサイズを1カラムにまとめる */}
          {(posterData.age || posterData.bust || posterData.waist || posterData.hip) && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">年齢・スリーサイズ:</Typography>
                <Typography variant="body2">
                  {posterData.age && `${posterData.age}歳`}
                  {(posterData.age && (posterData.bust || posterData.waist || posterData.hip)) && ` / `}
                  {posterData.height && `${posterData.height}cm / `}
                  {posterData.bust && `B:${posterData.bust}`}
                  {posterData.cup_size && `(${posterData.cup_size})`}
                  {posterData.waist && ` W:${posterData.waist}`}
                  {posterData.hip && ` H:${posterData.hip}`}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  // 投稿へのコメント処理
  const handleCommentPost = (postId) => {
    // 投稿オブジェクトを見つける
    const post = posts.find(p => p.id === postId);
    if (post) {
      // コメント数を更新
      post.comments_count = (post.comments_count || 0) + 1;
      // 投稿配列を更新
      setPosts([...posts]);
    }
    // 詳細ページへの遷移はしない
  };

  // 予約情報を取得する関数
  const fetchReservations = async (reset = true) => {
    if (!isCurrentUserProfile) return;
    
    try {
      console.log('=== 予約情報取得開始 ===', { reset });
      
      if (reset) {
        setReservationLoading(true);
        setReservations([]); // キャッシュクリア
      }
      
      // 認証トークンは apiService が自動で付与するため明示指定しない
      const response = await apiService.get('/api/users/reservations', {
        params: {
          _cache_bust: Date.now() // キャッシュ回避
        }
      });
      
      // apiService.get はレスポンスボディを直接返す
      const data = response?.data || response || {};
      const list = data.reservations || [];
      console.log('予約情報取得完了:', list.length);
      setReservations(list);
    } catch (error) {
      console.error('予約情報取得エラー:', error);
      setReservations([]);
    } finally {
      if (reset) {
        setReservationLoading(false);
      }
    }
  };
  
  // フォロー中の投稿ユーザーを検索する関数
  const searchFollowingPosters = async (query = '') => {
    try {
      setReservationLoading(true);
      // 認証トークンは apiService が自動で付与するため明示指定しない
      const response = await apiService.get(`/api/users/following/posters/search`, {
        params: {
          q: query,
          _t: new Date().getTime() // キャッシュ回避用のタイムスタンプ
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      // apiService.get はレスポンスボディを直接返す。
      // バックエンド(social.py)は { "users": [...] } を返す。
      const data = response?.data || response || {};
      const posters = data.users || data.posters || [];
      setFollowingPosters(posters);
    } catch (error) {
      console.error('フォロー中投稿ユーザー検索エラー:', error);
      // 取得失敗時はダミーを出さず空にする（誤ったユーザー表示を防ぐ）
      setFollowingPosters([]);
    } finally {
      setReservationLoading(false);
    }
  };
  
  // 開始時間(HH:mm)にコース分数を加算して終了時間(HH:mm)を返す
  const computeEndTime = (hhmm, minutes) => {
    if (!hhmm) return '';
    try {
      const [h, m] = hhmm.split(':').map(Number);
      let total = (h * 60 + m + Number(minutes)) % (24 * 60);
      const eh = Math.floor(total / 60);
      const em = total % 60;
      return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  // 投稿ユーザーを選択した時のハンドラ
  const handlePosterSelect = (poster) => {
    setSelectedPoster(poster);
    // ポップアップを閉じないように修正
  };

  // 選択中の投稿ユーザーのシフト一覧を取得（予約可能時間の表示用）
  useEffect(() => {
    if (!selectedPoster || !selectedPoster.id) {
      setSelectedPosterShifts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.get(`/api/shifts/user/${selectedPoster.id}`);
        const data = response?.data || response || {};
        if (!cancelled) setSelectedPosterShifts(data.shifts || []);
      } catch (e) {
        if (!cancelled) setSelectedPosterShifts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPoster]);

  // 選択中ユーザーの、指定日のシフト時間帯を「19:00～0:00(翌日)」の形式で返す
  const getSelectedPosterShiftLabel = () => {
    if (!selectedPoster || !reservationDate) return '';
    try {
      const dateStr = format(reservationDate, 'yyyy-MM-dd');
      const todays = selectedPosterShifts.filter((s) => s.date === dateStr);
      if (todays.length === 0) return '';
      return todays
        .map((s) => `${s.start_time}～${s.end_time}${s.is_next_day ? '(翌日)' : ''}`)
        .join('、');
    } catch {
      return '';
    }
  };
  
  // 予約ダイアログを開く
  const handleOpenReservationDialog = () => {
    searchFollowingPosters();
    setOpenReservationDialog(true);
  };

  // URLクエリ (?reserve=1&poster_id=...&date=...&start_time=...) が付いている場合、
  // 対象の投稿ユーザーと日時をプリセットして予約ダイアログを自動で開く。
  // シフトカレンダー／シフト投稿の「予約する」ボタンからの遷移で使用する。
  // 自分のプロフィール（予約タブを持つ閲覧ユーザー）でのみ有効。
  const reservePresetDoneRef = useRef(null);
  useEffect(() => {
    if (!isCurrentUserProfile || !profile) return;
    const params = new URLSearchParams(location.search);
    if (params.get('reserve') !== '1') return;

    // 同じクエリでは一度だけ処理する（再レンダリングでの多重オープンを防ぐ）
    if (reservePresetDoneRef.current === params.toString()) return;

    const posterId = params.get('poster_id') || '';
    if (!posterId) return;

    // 対象ユーザーをプリセット
    setSelectedPoster({
      id: posterId,
      username: params.get('poster_username') || '',
      display_name: params.get('poster_name') || params.get('poster_username') || '対象ユーザー',
      avatar: params.get('poster_avatar') || '',
    });
    // 日付をプリセット
    const dateStr = params.get('date');
    if (dateStr) {
      const d = new Date(`${dateStr}T00:00:00`);
      if (!isNaN(d)) setReservationDate(d);
    }
    // 開始時間をプリセット
    const st = params.get('start_time');
    if (st) setStartTime(st);

    // 予約タブを明示的にアクティブにする（タブ初期化のデフォルト適用より優先させ、
    // 一瞬モーダルが出た後に投稿タブへ戻る問題を防ぐ）
    setActiveTab('reservations');

    // ダイアログを開く（候補リスト検索も走らせておく）
    searchFollowingPosters();
    setOpenReservationDialog(true);

    // 同じクエリでの再オープンを一度きりにするためのフラグ。
    // クエリ書き換え(navigate)はタブ初期化 useEffect と競合するため行わない。
    reservePresetDoneRef.current = params.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentUserProfile, profile, location.search]);
  
  // 予約ダイアログを閉じる
  const handleCloseReservationDialog = () => {
    setOpenReservationDialog(false);
  };
  
  // 予約を作成する関数
  const handleCreateReservation = async () => {
    // 予約ダイアログがまだ開いていない場合は、ダイアログを開く
    if (!openReservationDialog) {
      handleOpenReservationDialog();
      return;
    }
    
    if (!selectedPoster) {
      alert('投稿ユーザーを選択してください');
      return;
    }
    
    if (!reservationDate) {
      alert('予約日を選択してください');
      return;
    }
    
    if (!startTime) {
      alert('開始時間を入力してください');
      return;
    }
    
    try {
      const formattedDate = format(reservationDate, 'yyyy-MM-dd');
      
      // 認証トークンは apiService が自動で付与するため明示指定しない
      // 終了時間はコース分数からサーバー側で自動計算される
      const response = await apiService.post('/api/users/reservations', {
        poster_id: selectedPoster.id,
        reservation_date: formattedDate,
        start_time: startTime,
        course_minutes: courseMinutes,
        hotel: hotel,
        memo: memo
      });
      
      // apiService.post はレスポンスボディを直接返す
      const data = response?.data || response || {};
      if (data.reservation) {
        // 新しい仮予約を予約リストに追加
        setReservations([data.reservation, ...reservations]);
        
        // フォームをリセット
        setSelectedPoster(null);
        setReservationDate(new Date());
        setStartTime('');
        setEndTime('');
        setCourseMinutes(60);
        setHotel('');
        setMemo('');
        
        // ダイアログを閉じる
        setOpenReservationDialog(false);
        
        alert('仮予約を送信しました。対象ユーザーの承認をお待ちください。');
      } else {
        // シフト不一致などのエラーメッセージを表示
        alert(data.message || data.error || '仮予約の作成に失敗しました');
      }
    } catch (error) {
      console.error('予約作成エラー:', error);
      // サーバーからのエラーメッセージ（シフト不一致など）を優先表示
      const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
      alert(serverMsg || '仮予約の作成に失敗しました');
    }
  };
  
  // キャンセル依頼を送信する関数（予約者＝閲覧ユーザー）
  const handleCancelRequestReservation = async (reservationId) => {
    if (!window.confirm('この予約のキャンセル依頼を送信しますか？（企業の確認後に確定します）')) {
      return;
    }
    
    try {
      const response = await apiService.post(`/api/users/reservations/${reservationId}/cancel-request`, {});
      const data = response?.data || response || {};
      // 依頼済みフラグを反映
      setReservations(reservations.map(r => r.id === reservationId ? { ...r, cancel_requested: true } : r));
      alert(data.message || 'キャンセル依頼を送信しました');
    } catch (error) {
      console.error('キャンセル依頼エラー:', error);
      alert(error?.response?.data?.message || 'キャンセル依頼の送信に失敗しました');
    }
  };

  // メッセージボタンのクリックハンドラーをシンプル化
  const handleMessageClick = () => {
    if (!isLoggedIn) {
      // 未ログインの場合はメッセージ表示（ゲストアクセス対応）
      alert('フォロー機能を利用するには会員登録またはログインが必要です。');
      return;
    }
    
    if (!isFollowing) {
      // フォローしていない場合は操作できないことを通知
      alert('メッセージを送るには、まずフォローする必要があります');
      return;
    }
    
    // メッセージページに遷移（クエリパラメータでユーザー名を渡す）
    navigate(`/messages?user=${username}`);
  };

  // プロフィール切り替え時の専用ローディング画面
  if (profileLoading) {
    return (
      <div className="profile">
        <div className="profile__header">
          <ArrowBackIcon className="profile__backButton" onClick={() => window.history.back()} />
          <div>
            <h2>プロフィールを読み込み中...</h2>
          </div>
        </div>
        <div className="profile__loading" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center'
        }}>
          <CircularProgress size={40} style={{ marginBottom: '20px' }} />
          <Typography variant="body1" style={{ color: '#666' }}>
            ユーザー情報を読み込んでいます...
          </Typography>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile__loading">
        <p>読み込み中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile__error" style={{ 
        padding: '20px', 
        textAlign: 'center', 
        margin: '40px auto', 
        maxWidth: '500px',
        backgroundColor: '#f8f8f8',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ color: '#e0245e', marginBottom: '10px' }}>
          {error.includes('非表示') ? 'ユーザーが非表示になっています' : 'エラーが発生しました'}
        </h3>
        <p>{error}</p>
        <Button variant="outlined" style={{ marginTop: '15px' }} onClick={() => navigate('/')}>
          ホームに戻る
        </Button>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="profile__header">
        <ArrowBackIcon className="profile__backButton" onClick={() => window.history.back()} />
        <div>
          <h2>
            {/* 投稿ユーザーの場合、「（源氏名）＠（所属店舗）」の形式で表示 */}
            {profile && (profile.role === 'poster' || profile.user_type === 'poster' || profile.poster_profile || profile.posterProfile) ? (
              <>
                {(profile.poster_profile?.stage_name || profile.posterProfile?.stage_name) ? (
                  `${profile.poster_profile?.stage_name || profile.posterProfile?.stage_name}`
                ) : (
                  `${profile?.display_name || profile?.displayName || username}`
                )}
                {profile?.verified && <VerifiedIcon className="profile__badge" />}
                {(profile.poster_profile?.shop_name || profile.posterProfile?.shop_name) && (
                  <>
                    ＠
                    {(
                      profile.poster_profile?.corporate_account || 
                      profile.posterProfile?.corporate_account || 
                      profile.poster_profile?.corporateAccount || 
                      profile.posterProfile?.corporateAccount || 
                      profile.poster_profile?.linked_corporate || 
                      profile.posterProfile?.linked_corporate || 
                      profile.poster_profile?.linkedCorporate || 
                      profile.posterProfile?.linkedCorporate
                    ) ? (
                      <Link to={`/profile/${
                        profile.poster_profile?.corporate_account || 
                        profile.posterProfile?.corporate_account || 
                        profile.poster_profile?.corporateAccount || 
                        profile.posterProfile?.corporateAccount || 
                        profile.poster_profile?.linked_corporate || 
                        profile.posterProfile?.linked_corporate || 
                        profile.poster_profile?.linkedCorporate || 
                        profile.posterProfile?.linkedCorporate
                      }`} style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted' }}>
                        {profile.poster_profile?.shop_name || profile.posterProfile?.shop_name}
                      </Link>
                    ) : (
                      <span>{profile.poster_profile?.shop_name || profile.posterProfile?.shop_name}</span>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {profile?.display_name || profile?.displayName || username}
                {profile?.verified && <VerifiedIcon className="profile__badge" />}
              </>
            )}
          </h2>
          <span className="profile__tweetCount">{totalPostsCount}件の投稿</span>
        </div>
      </div>

      <div className="profile__coverPhoto" style={{ 
        backgroundColor: (profile?.role === 'corporate' && profile?.corporateProfile?.bannerImage) || profile?.cover_image 
          ? 'transparent' 
          : '#1DA1F2' 
      }}>        
        {profile?.role === 'corporate' && profile?.corporateProfile?.bannerImage ? (
          <img             
            src={normalizeImageUrl(profile.corporateProfile.bannerImage)}             
            alt="企業バナー"             
            style={{               
              width: '100%',               
              objectFit: 'cover',              
              maxHeight: '250px'            
            }}          
          />
        ) : profile?.cover_image || (profile?.poster_profile && profile.poster_profile.cover_image) ? (
          <img             
            src={normalizeImageUrl((profile?.poster_profile && profile.poster_profile.cover_image) || profile?.cover_image)}             
            alt="カバー画像"             
            style={{               
              width: '100%',               
              objectFit: 'cover',              
              maxHeight: '200px'            
            }}          
          />
        ) : null}
      </div>

      <div className="profile__userInfo">
        <div className="profile__userInfoTop">
          <Avatar             
            src={profile?.role === 'corporate' && profile?.corporateProfile?.logoImage 
              ? normalizeImageUrl(profile.corporateProfile.logoImage)
              : normalizeImageUrl((profile?.poster_profile && profile.poster_profile.avatar) || profile?.avatar)}
            className="profile__avatar"            
            onClick={handleOpenAvatarModal}            
            style={{               
              cursor: 'pointer',              
              width: profile?.role === 'corporate' ? '120px' : '80px',              
              height: profile?.role === 'corporate' ? '120px' : '80px',              
              border: '4px solid white',              
              marginTop: profile?.role === 'corporate' ? '-40px' : '-30px',              
              backgroundColor: 'white'            
            }}            
            alt={profile?.role === 'corporate' ? "企業ロゴ" : "プロフィール画像"}          
          />
          <div className="profile__buttonContainer">
            {isCurrentUserProfile && (
              <button className="profile__editButton" onClick={handleEditProfile}>プロフィールを編集</button>
            )}
            
            {/* 管理者/モデレーター用のダッシュボードボタン */}
            {isCurrentUserProfile && checkPermission('ADMIN_FUNCTIONS') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DashboardIcon />}
                onClick={() => navigate('/dashboard')}
                className="profile__dashboardButton"
                sx={{ ml: 1 }}
              >
                ダッシュボード
              </Button>
            )}
            
            {!isCurrentUserProfile && isLoggedIn && (
              <>
                {/* メッセージボタン - 企業ユーザーまたは投稿ユーザーの場合のみ表示 */}
                {profile && (profile.role === 'corporate' || profile.role === 'poster' || 
                 profile.user_type === 'poster' || profile.poster_profile || profile.posterProfile) && (
                  <IconButton
                    variant="outlined"
                    color="primary"
                    onClick={handleMessageClick}
                    disabled={!isFollowing}
                    sx={{ mr: 1, border: '1px solid', borderColor: 'primary.main' }}
                    title="メッセージ"
                  >
                    <ChatIcon />
                  </IconButton>
                )}
                
                {/* フォローボタン */}
                <button 
                  className={`profile__followButton ${isFollowing ? 'profile__followButton--following' : ''}`}
                  onClick={handleFollowToggle}
                  disabled={isFollowingLoading}
                >
                  {isFollowingLoading ? '処理中...' : (isFollowing ? 'フォロー中' : 'フォローする')}
                </button>
              </>
            )}
            
            {/* 三点メニュー - 自分以外のプロフィールを見ているときのみ表示 */}
            {!isCurrentUserProfile && isLoggedIn && (
              <div>
                <IconButton 
                  aria-label="プロフィールメニュー"
                  aria-controls={menuOpen ? 'profile-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? 'true' : undefined}
                  onClick={handleMenuClick}
                  sx={{ ml: 1 }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="profile-menu"
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'profile-menu-button',
                  }}
                >
                  {/* ブロック/ブロック解除ボタン - ゲスト、閲覧ユーザー以外に表示 */}
                  {canBlockUser() && (
                    isBlocked ? (
                      <MenuItem onClick={handleUnblockUser} disabled={blockLoading}>
                        <PersonAddIcon fontSize="small" sx={{ mr: 1 }} />
                        {blockLoading ? '処理中...' : 'ブロック解除'}
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={handleBlockUser} disabled={blockLoading}>
                        <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />
                        {blockLoading ? '処理中...' : 'ユーザーをブロック'}
                      </MenuItem>
                    )
                  )}
                  
                  <MenuItem onClick={handleReport}>
                    <ReportIcon fontSize="small" sx={{ mr: 1 }} />
                    このユーザーを通報
                  </MenuItem>
                </Menu>
              </div>
            )}
          </div>
        </div>

        <div className="profile__userInfoDetails">
          <h2>
            {/* 投稿ユーザーの場合、「（源氏名）＠（所属店舗）」の形式で表示 */}
            {profile && (profile.role === 'poster' || profile.user_type === 'poster' || profile.poster_profile || profile.posterProfile) ? (
              <>
                {(profile.poster_profile?.stage_name || profile.posterProfile?.stage_name) ? (
                  `${profile.poster_profile?.stage_name || profile.posterProfile?.stage_name}`
                ) : (
                  `${profile?.display_name || profile?.displayName || username}`
                )}
                {profile?.verified && <VerifiedIcon className="profile__badge" />}
                {(profile.poster_profile?.shop_name || profile.posterProfile?.shop_name) && (
                  <>
                    ＠
                    {(
                      profile.poster_profile?.corporate_account || 
                      profile.posterProfile?.corporate_account || 
                      profile.poster_profile?.corporateAccount || 
                      profile.posterProfile?.corporateAccount || 
                      profile.poster_profile?.linked_corporate || 
                      profile.posterProfile?.linked_corporate || 
                      profile.poster_profile?.linkedCorporate || 
                      profile.posterProfile?.linkedCorporate
                    ) ? (
                      <Link to={`/profile/${
                        profile.poster_profile?.corporate_account || 
                        profile.posterProfile?.corporate_account || 
                        profile.poster_profile?.corporateAccount || 
                        profile.posterProfile?.corporateAccount || 
                        profile.poster_profile?.linked_corporate || 
                        profile.posterProfile?.linked_corporate || 
                        profile.poster_profile?.linkedCorporate || 
                        profile.posterProfile?.linkedCorporate
                      }`} style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted' }}>
                        {profile.poster_profile?.shop_name || profile.posterProfile?.shop_name}
                      </Link>
                    ) : (
                      <span>{profile.poster_profile?.shop_name || profile.posterProfile?.shop_name}</span>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {profile?.display_name || profile?.displayName || username}
                {profile?.verified && <VerifiedIcon className="profile__badge" />}
              </>
            )}
          </h2>
          <p className="profile__username">@{profile?.username}</p>
          
          {profile?.is_hidden ? (
            <div className="profile__hidden" style={{ backgroundColor: '#f5f5f5', padding: '20px', textAlign: 'center', borderRadius: '8px', margin: '10px 0' }}>
              <p style={{ color: '#999', fontWeight: 'bold', fontSize: '1.1em' }}>
                このアカウントは管理者により非表示に設定されています
              </p>
            </div>
          ) : profile?.is_locked ? (
            <div className="profile__locked" style={{ backgroundColor: '#fff3f3', padding: '12px', borderRadius: '8px', border: '1px solid #ffcdd2', margin: '10px 0' }}>
              <p className="profile__lockedMessage" style={{ color: '#d32f2f', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                ⚠️ このアカウントはロック中です
              </p>
              {profile?.locked_reason && (
                <p className="profile__lockedReason" style={{ color: '#555', fontStyle: 'italic', margin: '0', fontSize: '0.9em' }}>
                  理由: {profile?.locked_reason}
                </p>
              )}
            </div>
          ) : profile?.bio ? (
            <p className="profile__bio">{profile?.bio}</p>
          ) : null}
          
          <div className="profile__userMeta">
            {profile?.location && (
              <span className="profile__userMetaItem">
                <LocationOnIcon className="profile__userMetaIcon" />
                {profile?.location}
              </span>
            )}
            
            {profile?.website && (
              <span className="profile__userMetaItem">
                <LinkIcon className="profile__userMetaIcon" />
                <a href={profile?.website} target="_blank" rel="noopener noreferrer">
                  {profile?.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              </span>
            )}
            
            <span className="profile__userMetaItem">
              <DateRangeIcon className="profile__userMetaIcon" />
              {moment(profile?.joinedDate).format('YYYY年M月')}に登録
            </span>
          </div>
          
          <div className="profile__followInfo">
            {/* ログイン時のみフォロー数・フォロワー数を表示 */}
            {isLoggedIn && (
              <>
                {canAccessFollowingList() ? (
                  <Link 
                    to={`/profile/${username}/following`}
                    className="profile__followingLink"
                    onClick={(e) => e.stopPropagation()}
                  >
                    フォロー中 <strong>{followingCount}</strong>
                  </Link>
                ) : (
                  <>フォロー中 <strong>{followingCount}</strong></>
                )}
                {' '}
                {canAccessFollowersList() ? (
                  <Link 
                    to={`/profile/${username}/followers`}
                    className="profile__followersLink"
                    onClick={(e) => e.stopPropagation()}
                  >
                    フォロワー <strong>{followersCount}</strong>
                  </Link>
                ) : (
                  <>フォロワー <strong>{followersCount}</strong></>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* 企業プロフィール情報を表示 - profile__userInfoの下に移動 */}
        {profile && profile.role === 'corporate' && profile.corporateProfile && (
          <div className="corporate-profile-section" style={{ marginTop: '20px', padding: '10px 0' }}>
            <Grid container spacing={2}>
              {/* 業種 */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">業種:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {profile.corporateProfile.industry && profile.corporateProfile.industry.map((ind, index) => (
                      <Chip key={index} label={ind} color="primary" variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              </Grid>
              
              {/* 営業時間 */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ScheduleIcon fontSize="small" sx={{ mr: 0.5 }} />
                    営業時間:
                  </Typography>
                  <Typography variant="body2">
                    {profile.corporateProfile.businessHours?.start || '設定なし'} 〜 {profile.corporateProfile.businessHours?.end || '設定なし'}
                  </Typography>
                </Box>
              </Grid>
              
              {/* 電話番号 */}
              {profile.corporateProfile.phoneNumber && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} />
                      電話番号:
                    </Typography>
                    <Typography variant="body2">
                      <a 
                        href={`tel:${profile.corporateProfile.phoneNumber}`}
                        style={{
                          color: '#1976d2',
                          fontWeight: 'bold',
                          textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                      {profile.corporateProfile.phoneNumber}
                      </a>
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {/* 都道府県と市区町村 */}
              {profile.corporateProfile.prefecture && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                      エリア:
                    </Typography>
                    <Typography variant="body2">
                      {profile.corporateProfile.prefecture.name}
                      {profile.corporateProfile.cities && profile.corporateProfile.cities.length > 0 && (
                        <>
                          （
                          {profile.corporateProfile.cities.map((city, index) => (
                            <React.Fragment key={city.code}>
                              {index > 0 && '、'}
                              {city.name}
                            </React.Fragment>
                          ))}
                          ）
                        </>
                      )}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {/* オフィシャルLINE */}
              {(profile.corporateProfile.officialLine || profile.corporateProfile.official_line) && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <ChatIcon fontSize="small" sx={{ mr: 0.5 }} />
                      オフィシャルLINE:
                    </Typography>
                    <a 
                      href={profile.corporateProfile.officialLine || profile.corporateProfile.official_line} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        wordBreak: 'break-all',
                        color: '#00B900',
                        fontWeight: 'bold',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      🔗 公式LINEを開く
                    </a>
                  </Box>
                </Grid>
              )}
              
              {/* 求人情報 */}
              {(profile.corporateProfile.jobPostingUrl || profile.corporateProfile.job_posting_url) && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <WorkIcon fontSize="small" sx={{ mr: 0.5 }} />
                      求人情報:
                    </Typography>
                    <a 
                      href={profile.corporateProfile.jobPostingUrl || profile.corporateProfile.job_posting_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        wordBreak: 'break-all',
                        color: '#1976d2',
                        fontWeight: 'bold',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      🔗 求人情報を見る
                    </a>
                  </Box>
                </Grid>
              )}
            </Grid>
          </div>
        )}

        {/* 投稿ユーザープロフィール情報を表示 - profile__userInfoの下に移動 */}
        {profile && (
          (profile.role === 'poster' || profile.user_type === 'poster' || 
           profile.poster_profile || profile.posterProfile)
        ) && (
          <div className="poster-profile-section" style={{ marginTop: '20px', padding: '15px', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
            {/* 「投稿者情報」というタイトルを削除 */}
            
            <Grid container spacing={2}>
              {/* 「ユーザー種別: poster」の表示も削除 */}
              
              {/* 店舗名 - posterProfileをチェック */}
              {(profile.poster_profile?.shop_name || profile.posterProfile?.shop_name) && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} />
                      所属店舗:
                    </Typography>
                    <Typography variant="body2">
                      {(
                        profile.poster_profile?.corporate_account || 
                        profile.posterProfile?.corporate_account || 
                        profile.poster_profile?.corporateAccount || 
                        profile.posterProfile?.corporateAccount || 
                        profile.poster_profile?.linked_corporate || 
                        profile.posterProfile?.linked_corporate || 
                        profile.poster_profile?.linkedCorporate || 
                        profile.posterProfile?.linkedCorporate
                      ) ? (
                        <Link to={`/profile/${
                          profile.poster_profile?.corporate_account || 
                          profile.posterProfile?.corporate_account || 
                          profile.poster_profile?.corporateAccount || 
                          profile.posterProfile?.corporateAccount || 
                          profile.poster_profile?.linked_corporate || 
                          profile.posterProfile?.linked_corporate || 
                          profile.poster_profile?.linkedCorporate || 
                          profile.posterProfile?.linkedCorporate
                        }`} style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted' }}>
                          {profile.poster_profile?.shop_name || profile.posterProfile?.shop_name}
                        </Link>
                      ) : (
                        profile.poster_profile?.shop_name || profile.posterProfile?.shop_name
                      )}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {/* 都道府県・市区町村 */}
              {(profile.poster_profile?.prefecture || profile.poster_profile?.city || 
                profile.posterProfile?.prefecture || profile.posterProfile?.city) && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                      エリア:
                    </Typography>
                    <Typography variant="body2">
                      {(profile.poster_profile?.prefecture?.name || profile.posterProfile?.prefecture?.name || '')}
                      {(profile.poster_profile?.city?.name || profile.posterProfile?.city?.name) && 
                        ` (${profile.poster_profile?.city?.name || profile.posterProfile?.city?.name})`}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {/* 芸名表示を削除 */}
              
              {/* 年齢・スリーサイズを1カラムにまとめる */}
              {((profile.poster_profile?.age || profile.posterProfile?.age) || 
                (profile.poster_profile?.bust || profile.poster_profile?.waist || profile.poster_profile?.hip ||
                 profile.posterProfile?.bust || profile.posterProfile?.waist || profile.posterProfile?.hip)) && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">年齢・スリーサイズ:</Typography>
                    <Typography variant="body2">
                      {(profile.poster_profile?.age || profile.posterProfile?.age) && 
                        `${profile.poster_profile?.age || profile.posterProfile?.age}歳`}
                      {((profile.poster_profile?.age || profile.posterProfile?.age) && 
                        (profile.poster_profile?.bust || profile.poster_profile?.waist || profile.poster_profile?.hip ||
                         profile.posterProfile?.bust || profile.posterProfile?.waist || profile.posterProfile?.hip)) && 
                        ` / `}
                      {(profile.poster_profile?.height || profile.posterProfile?.height) && 
                        `${profile.poster_profile?.height || profile.posterProfile?.height}cm / `}
                      {(profile.poster_profile?.bust || profile.posterProfile?.bust) && 
                        `B:${profile.poster_profile?.bust || profile.posterProfile?.bust}`}
                      {(profile.poster_profile?.cup_size || profile.posterProfile?.cup_size) && 
                        `(${profile.poster_profile?.cup_size || profile.posterProfile?.cup_size})`}
                      {(profile.poster_profile?.waist || profile.posterProfile?.waist) && 
                        ` W:${profile.poster_profile?.waist || profile.posterProfile?.waist}`}
                      {(profile.poster_profile?.hip || profile.posterProfile?.hip) && 
                        ` H:${profile.poster_profile?.hip || profile.posterProfile?.hip}`}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </div>
        )}
      </div>

      <div className="profile__nav" ref={tabsContainerRef} style={{ display: 'flex', width: '100%' }}>
        {/* ユーザータイプに応じてタブを表示 */}
        {profile && (
          <>
            {/* 1. 閲覧ユーザーの場合：返信、いいね、予約 */}
            {profile.role === 'viewer' ? (
              <>
                <div
                  className={`profile__navItem ${activeTab === 'replies' ? 'active' : ''}`}
                  onClick={() => handleTabChange('replies')}
                  style={{ flex: 1, textAlign: 'center' }}
                >
                  返信
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'likes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('likes')}
                  style={{ flex: 1, textAlign: 'center' }}
                >
                  いいね
                </div>
                {isCurrentUserProfile && (
                  <div
                    className={`profile__navItem ${activeTab === 'reservations' ? 'active' : ''}`}
                    onClick={() => handleTabChange('reservations')}
                    style={{ flex: 1, textAlign: 'center' }}
                  >
                    予約
                  </div>
                )}
              </>
            ) : 
            /* 2. 投稿ユーザーの場合：投稿、返信、メディア、いいね、シフト */
            (profile.role === 'poster' || profile.user_type === 'poster' || 
             profile.poster_profile || profile.posterProfile) ? (
              <>
                <div
                  className={`profile__navItem ${activeTab === 'posts' ? 'active' : ''}`}
                  onClick={() => handleTabChange('posts')}
                >
                  投稿
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'replies' ? 'active' : ''}`}
                  onClick={() => handleTabChange('replies')}
                >
                  返信
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'media' ? 'active' : ''}`}
                  onClick={() => handleTabChange('media')}
                >
                  メディア
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'likes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('likes')}
                >
                  いいね
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'shifts' ? 'active' : ''}`}
                  onClick={() => handleTabChange('shifts')}
                >
                  シフト
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => handleTabChange('reviews')}
                >
                  クチコミ
                </div>
              </>
            ) :
            /* 3. 企業ユーザーの場合：投稿、返信、メディア、いいね、リスト */
            profile.role === 'corporate' ? (
              <>
                <div
                  className={`profile__navItem ${activeTab === 'posts' ? 'active' : ''}`}
                  onClick={() => handleTabChange('posts')}
                >
                  投稿
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'replies' ? 'active' : ''}`}
                  onClick={() => handleTabChange('replies')}
                >
                  返信
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'media' ? 'active' : ''}`}
                  onClick={() => handleTabChange('media')}
                >
                  メディア
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'likes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('likes')}
                >
                  いいね
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'lists' ? 'active' : ''}`}
                  onClick={() => handleTabChange('lists')}
                >
                  リスト
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => handleTabChange('reviews')}
                >
                  クチコミ
                </div>
              </>
            ) :
            /* 4. 管理者などの場合：投稿、返信、メディア、いいね、リスト */
            (currentUser && currentUser.role === 'admin') ? (
              <>
                <div
                  className={`profile__navItem ${activeTab === 'posts' ? 'active' : ''}`}
                  onClick={() => handleTabChange('posts')}
                >
                  投稿
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'replies' ? 'active' : ''}`}
                  onClick={() => handleTabChange('replies')}
                >
                  返信
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'media' ? 'active' : ''}`}
                  onClick={() => handleTabChange('media')}
                >
                  メディア
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'likes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('likes')}
                >
                  いいね
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'lists' ? 'active' : ''}`}
                  onClick={() => handleTabChange('lists')}
                >
                  リスト
                </div>
              </>
            ) :
            /* 5. その他のユーザー（一般ユーザーなど）*/
            (
              <>
                <div
                  className={`profile__navItem ${activeTab === 'posts' ? 'active' : ''}`}
                  onClick={() => handleTabChange('posts')}
                >
                  投稿
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'replies' ? 'active' : ''}`}
                  onClick={() => handleTabChange('replies')}
                >
                  返信
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'media' ? 'active' : ''}`}
                  onClick={() => handleTabChange('media')}
                >
                  メディア
                </div>
                <div
                  className={`profile__navItem ${activeTab === 'likes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('likes')}
                >
                  いいね
                </div>
                {/* 公開リストがある場合はリストタブを表示 */}
                <div
                  className={`profile__navItem ${activeTab === 'lists' ? 'active' : ''}`}
                  onClick={() => handleTabChange('lists')}
                >
                  リスト
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="profile__posts">
        {getTabContent()}
      </div>

      {/* アバターの拡大モーダル */}
      <Modal
        open={avatarModalOpen}
        onClose={handleCloseAvatarModal}
        aria-labelledby="avatar-modal"
      >
        <Box sx={modalStyle}>
          <img
            src={profile?.role === 'corporate' && profile?.corporateProfile?.logoImage 
              ? normalizeImageUrl(profile.corporateProfile.logoImage)
              : normalizeImageUrl((profile?.poster_profile && profile.poster_profile.avatar) || profile?.avatar)}
            alt={profile?.display_name || profile?.username || "プロフィール画像"}
            style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </Box>
      </Modal>
    </div>
  );
}

export default Profile;

// 投稿ユーザーのシフトカレンダーコンポーネント
const ShiftCalendar = ({ userId, username, displayName, avatar }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedShifts, setSelectedShifts] = useState([]);
  const { token, currentUser } = useAuth();
  const navigate = useNavigate();
  
  const isCurrentUserProfile = currentUser && currentUser.username === username;
  
  // シフト情報を取得
  useEffect(() => {
    const fetchShifts = async () => {
      try {
        setLoading(true);
        const response = await apiService.get(`/api/shifts/user/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        // レスポンスからshifts配列を取得
        setShifts(response.data?.shifts || []);
      } catch (err) {
        
        setError('シフト情報を取得できませんでした');
      } finally {
        setLoading(false);
      }
    };
    
    if (userId) {
      fetchShifts();
    }
  }, [userId, token]);
  
  // 日付選択時の処理
  useEffect(() => {
    if (selectedDate && shifts.length > 0) {
      // 選択された日付のシフトを抽出
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const shiftsForSelectedDate = shifts.filter(
        shift => shift.date === formattedDate
      );
      setSelectedShifts(shiftsForSelectedDate);
    } else {
      setSelectedShifts([]);
    }
  }, [selectedDate, shifts]);
  
  // カレンダーヘッダーの曜日を表示
  const renderDayNames = () => {
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    return (
      <div className="calendar-day-names">
        {days.map((day, index) => (
          <div 
            key={`day-${index}`} 
            className={`calendar-day-name ${index === 0 ? 'sunday' : index === 6 ? 'saturday' : ''}`}
          >
            {day}
          </div>
        ))}
      </div>
    );
  };
  
  // カレンダーの日付セルを表示
  const renderDays = () => {
    const days = [];
    // 月の最初の日
    const firstDay = startOfMonth(currentMonth);
    // 月の最後の日
    const lastDay = endOfMonth(currentMonth);
    // 表示する最初の日（当月の1日より前の日曜日から表示）
    const startDate = startOfWeek(firstDay);
    // 表示する最後の日（当月の末日以降の土曜日まで表示）
    const endDate = endOfWeek(lastDay);
    
    // 日付を順に処理
    let day = startDate;
    while (day <= endDate) {
      const currentDate = new Date(day);
      const formattedDate = format(currentDate, 'yyyy-MM-dd');
      const isCurrentDay = isSameDay(day, new Date());
      const isSelectedDay = isSameDay(day, selectedDate);
      const isCurrentMonth = isSameMonth(day, currentMonth);
      const hasShift = shifts.some(shift => shift.date === formattedDate);
      
      days.push(
        <div
          key={day.toString()}
          className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isCurrentDay ? 'current-day' : ''} ${isSelectedDay ? 'selected-day' : ''} ${hasShift ? 'has-shift' : ''}`}
          onClick={() => setSelectedDate(currentDate)}
        >
          {format(day, 'd')}
          {hasShift && <div className="shift-indicator"></div>}
        </div>
      );
      
      day = addDays(day, 1);
    }
    
    return <div className="calendar-days">{days}</div>;
  };
  
  // 前月に移動
  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };
  
  // 翌月に移動
  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };
  
  // 選択された日付のシフト情報表示
  const renderSelectedDateShifts = () => {
    if (selectedShifts.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2">
            この日のシフト情報はありません
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          {format(selectedDate, 'yyyy年MM月dd日 (EEE)', { locale: ja })}のシフト
        </Typography>
        
        {selectedShifts.map((shift, index) => (
          <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant="body1">
              {shift.start_time} - {shift.end_time}
              {shift.is_next_day && ' (翌日)'}
            </Typography>
            {/* 予約導線は閲覧ユーザー(viewer)と管理者(admin)のみに表示。
                投稿ユーザー・企業ユーザーには表示しない。 */}
            {!isCurrentUserProfile && currentUser && (currentUser.role === 'viewer' || currentUser.role === 'admin') && (
              <Button
                variant="contained"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  // 自分のプロフィールの予約タブへ、対象ユーザー・日時をクエリで渡して遷移
                  const params = new URLSearchParams({
                    tab: 'reservations',
                    reserve: '1',
                    poster_id: userId || '',
                    poster_username: username || '',
                    poster_name: displayName || username || '',
                    poster_avatar: avatar || '',
                    date: shift.date || '',
                    start_time: shift.start_time || '',
                  });
                  navigate(`/profile/${currentUser.username}?${params.toString()}`);
                }}
              >
                この日時で予約
              </Button>
            )}
          </Box>
        ))}
      </Box>
    );
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      {isCurrentUserProfile && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/shifts')}
          >
            シフトを管理する
          </Button>
        </Box>
      )}
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handlePrevMonth}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">
            {format(currentMonth, 'yyyy年MM月')}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIcon />
          </IconButton>
        </Box>
        
        <Box className="calendar-container">
          {renderDayNames()}
          {renderDays()}
        </Box>
      </Paper>
      
      <Paper sx={{ p: 2 }}>
        {renderSelectedDateShifts()}
      </Paper>
    </Box>
  );
};

// シフト情報を取得する関数を追加
const fetchShifts = (reset = true) => {
  console.log('=== シフト情報取得 ===', { reset });
  // ShiftCalendarコンポーネントで取得するため、ここではログ出力のみ
};
